// Non-Haven Imports

import _ from 'lodash';
import * as Url from 'url';
import Analytics from 'analytics-node';
import safeJsonStringify from 'safe-json-stringify';
import jwt from 'jsonwebtoken';
import * as uuid from 'uuid';
import * as stream from 'stream';
import { Request, Response } from 'express';

// Haven Module Imports
import {
  amorphicStatic,
  IAmorphicAppController,
  property,
  remote,
  Supertype,
  supertypeClass,
} from '@haventech/amorphic';
import { MetadataApi, MetadataCategory } from '@hit/metadata-module';
import { CallContext, PreServerCallChanges } from '@haventech/amorphic/dist/lib/types/HelperTypes';
import { HTTPObjs, RemoteCall } from '@haventech/amorphic/dist/lib/types/ILifecycleController';

// Internal Imports
import {
  Auth0Controller,
  AuthenticationInformation,
  IdentifiedPrincipal,
} from '../../../common/js/authentication/Auth0Controller';
import { AuthenticatedPrincipal } from '../../../common/js/AuthenticatedPrincipal';
import { AuthOwner, Customer } from '../../../common/js/customer/Customer';
import { Policy } from '../../../common/js/customer/Policy';
import { CoverpathControllerInterface } from '../../../coverpath-common/angular/interfaces/controller/CoverpathControllerInterface';
import { TFAController } from '../../../coverpath-common/js/controllers/TFAController';
import { ApplicationController } from '../../../coverpath-common/angular/amorphicControllers/ApplicationController';
import { Utils } from '../../../common/js/Utils';
import {
  PremiumRequest,
  PremiumRequestList,
  PremiumResponse,
  PremiumResponseList,
  ProductServiceClient,
  ProductSpecRequest,
  ProductSpecResponse,
} from '../../../common/js/productService/ProductServiceClient';
import { WorkflowHelper } from '../../../coverpath-common/classes/WorkflowHelper';
import { EmailNotificationController } from './amorphicControllers/EmailNotificationController';
import { AccountCenterController } from './amorphicControllers/AccountCenterController';
import { InviteController } from './amorphicControllers/InviteController';
import { ProductRulesController } from '../../../common/js/ProductRulesController';
import { Emailer } from '../../../common/js/services/emailer/Emailer';
import { EmailParams } from '../../../common/js/interfaces/EmailParams';
import { Product } from '../../../common/js/static/Product';
import { LoginType } from '../../../coverpath-common/angular/classes/LoginType';
import { PolicyAdminUtils } from '../../../common/js/policyAdmin/PolicyAdminUtils';
import { PolicyAdminClient } from '../../../common/js/policyAdmin/PolicyAdminClient';

import { DefaultQuote } from './classes/DefaultCoverpathClientQuote';
import {
  AnnuityAdditionalSigners,
  ApplicationTypes,
  CoverpathCustomerProps,
  Entitlement,
  FeatureFlags,
  LockedByUserType,
  PreServerDataLog,
  ProductQuoteResponse,
  ProductQuoteResponseList,
  SiteType,
  ValidateDocumentAccess,
} from '../../../coverpath-common/js/interfaces/CoverpathTypes';
import { HtmlPdfController } from '../../../coverpath-common/classes/wholelife/basicIllustration/pdf/HtmlPdfController';
import { CoverpathTermQuote } from '../../../coverpath-common/classes/quote/coverpath/CoverpathTermQuote';
import { BaseQuote } from '../../../common/js/quote/BaseQuote';
import { SubmittedPolicyValidatingController } from '../../../common/js/controller/SubmittedPolicyValidatingController';
import { CommissionSplitsController } from './amorphicControllers/CommissionSplitsController';
import { PrincipalCustomer } from '../../../common/js/PrincipalCustomer';
import { Identity } from '../../../common/js/account/identity/Identity';
import { LaunchDarklyInstance, LD_APP } from '../../../common/js/launch-darkly/LaunchDarklySingleton';
import { Auth0Channel, Auth0Configs, Auth0Utils } from '../../../common/js/authentication/Auth0Utils';
import { AMCAuth0ResponseUser, Auth0Client, Auth0Response } from '../../../common/js/authentication/Auth0Client';
import { AppConfigTypes } from '../../../common/js/config/AppConfig';
import { RemoteDocServiceController } from '../../../common/js/controller/RemoteDocServiceController';
import { CoverpathUtils } from '../../../coverpath-common/js/static/CoverpathUtils';
import { Person } from '../../../common/js/customer/Person';
import { AMCController } from '../../../common/js/authentication/amc/AMCController';
import { Account } from '../../../common/js/account/Account';
import { AccountAuthorization } from '../../../common/js/account/authorization/AccountAuthorization';
import * as AuthenticationHelpers from '../../../common/js/AuthenticationHelpers';

import { ApplicationDataService } from '../../../coverpath-common/js/static/ApplicationDataService';
import { ApplicationLockController } from '../../../coverpath-common/js/controllers/ApplicationLockController';
import { HitAppCtx } from '../../../common/js/orca/HitAppCtx';
import { PolicyAgent } from '../../../common/js/customer/PolicyAgent';
import { AuditEvent } from '../../../common/js/audit/AuditEvent';
import { ApplicationSourceType } from '../../../common/js/customer/Application';
import { AgentLicense } from '../../../common/js/agent/AgentLicense';
import { AgencyCorpsRelationship, CdmService } from '../../../common/js/services/CdmService';
import { CoverpathCustomer } from '../../../coverpath-common/classes/coverpath_subclasses/CoverpathCustomer';
import { PolicyDoc } from '../../../common/js/customer/PolicyDoc';
import { POLICY_DOC_ACTION_TYPE } from '../../../common/js/static/PolicyDocActionType';
import { ESignSessionMetadata } from '../../../common/js/customer/ESignSessionMetadata';
import { CoverpathCustomerUtils } from '../../../coverpath-common/js/static/CoverpathCustomerUtils';
import '../../../coverpath-common/classes/coverpath_subclasses/CoverpathAnnuityApplication';
import '../../../coverpath-common/classes/coverpath_subclasses/CoverpathAnnuity';
import { CoverpathPolicy } from '../../../coverpath-common/classes/coverpath_subclasses/CoverpathPolicy';
import { CoverpathBasePolicy } from '../../../coverpath-common/classes/coverpath_subclasses/CoverpathBasePolicy';
import { CasApplication } from '../../../coverpath-common/classes/coverpath_subclasses/CasApplication';
import { ProductClientController } from '../../../coverpath-common/js/controllers/ProductClientController';
import { CoverpathUtilsBackEnd } from '../../../coverpath-common/js/static/CoverpathUtilsBackEnd';
import { IllustrationRequest } from '../../../coverpath-common/classes/wholelife/IllustrationRequest';
import { HtmlPdfContentController } from '../../../coverpath-common/classes/wholelife/HtmlPdfContentController';
import { CoverpathAnnuity } from '../../../coverpath-common/classes/coverpath_subclasses/CoverpathAnnuity';

import { AnnuityDocuments } from '../../../coverpath-common/js/static/annuity-app-package/AnnuityDocuments';
import { serverOnly } from '../../../common/js/helpers';
import { SanderlingPolicyRepairController } from './amorphicControllers/SanderlingPolicyRepairController';
import { LoggedInUserInfo, StreamableDoc, UserEntitlement } from '../../../common/js/interfaces/CommonTypes';
import { CommonUtilsBackend } from '../../../common/js/static/CommonUtilsBackend';
import { MMDirectApplication } from '../../../coverpath-common/classes/mmdirect_subclasses/MMDirectApplication';
import { SecurityUtils } from '../../../coverpath-common/js/static/SecurityUtils';
import { GiactController } from '../../../coverpath-common/js/controllers/GiactController';
import { IllustrationRequestHelpers } from '../../../coverpath-common/classes/wholelife/IllustrationRequestHelpers';
import { SafeInviteInfo } from '../../../common/js/customer/SafeInviteInfo';
import { VSTResult } from '../../../coverpath-common/advisor-sales-tools/interfaces/VST';
import { QuoteForm } from '../../../coverpath-agent/public/js/components/home-new/quote-or-apply-new/QuoteForm';
import { POLICY_TYPES } from '../../../common/js/constants/policy';
import { Agent } from '../../../common/js/customer/Agent';
import { ObjectGraphController } from '../../../coverpath-common/controllers/ObjectGraphController';
import { LoggerSingleton, Category, PiiLevelEnum } from '../../../common/js/logger/LoggerSingleton';
import { UwClientAppCtx } from '@hit/underwriting-api-client';
import { UICategory, UILoggerSingleton, UIPiiLevelEnum } from '../../../common/js/logger/UILoggerSingleton';

declare let window;
amorphicStatic['toClientRuleSet'] = ['coverpath-client'];

@supertypeClass({ toClient: true, toServer: true })
export class Controller extends Auth0Controller implements CoverpathControllerInterface, IAmorphicAppController {
  static readonly CLIENT_SITE_AUDITING_ENABLED_FLAG = 'clientSiteAuditing';

  @property()
  customer: CoverpathCustomer;

  @property()
  workflowHelper: WorkflowHelper;

  @property()
  tfaController: TFAController;

  @property()
  amcController: AMCController;

  @property({ toClient: false, toServer: false, getType: () => SanderlingPolicyRepairController })
  sanderlingPolicyRepairController: SanderlingPolicyRepairController;

  @property()
  emailNotificationController: EmailNotificationController;

  @property({ getType: () => HtmlPdfController })
  htmlPdfController: HtmlPdfController;

  @property({ getType: () => HtmlPdfContentController })
  htmlPdfContentController: HtmlPdfContentController;

  @property()
  accountCenterController: AccountCenterController;

  @property()
  applicationLockController: ApplicationLockController;

  @property({ getType: () => InviteController })
  inviteController: InviteController;

  @property()
  applicationController: ApplicationController;

  @property()
  productRulesController: ProductRulesController;

  @property()
  commissionSplitsController: CommissionSplitsController;

  @property({ getType: () => ProductClientController })
  productClientController: ProductClientController;

  @property({ toClient: false, getType: () => SubmittedPolicyValidatingController })
  submittedPolicyValidatingController: SubmittedPolicyValidatingController;

  @property({ getType: () => GiactController })
  giactController: GiactController;

  @property({ toClient: false, getType: () => Emailer })
  emailer: Emailer;

  @property({ toClient: false, rule: ['required', 'password'], sensitiveData: true })
  newPassword: string = '';

  @property({ toClient: false, rule: ['required'], sensitiveData: true })
  password: string = '';

  @property({ toServer: false })
  nonProdMode: boolean;

  @property({ toServer: false })
  use2019Filing: boolean = false;

  @property({ toServer: false })
  segmentKey: string;

  @property({ toServer: false })
  sendToSegment: boolean;

  @property({ toServer: false })
  isJuvenilePolicyEnabled: boolean;

  @property()
  remoteDocServiceController: RemoteDocServiceController;

  @property({ toServer: false })
  is1035ExchangeEnabled: boolean;

  @property({ toServer: false })
  isNwmReplacementsEnabled: boolean;

  @property({ toServer: false })
  artEnabled: boolean = false;

  @property({ toServer: false })
  isLocked: boolean = false;

  @property({ toServer: false })
  lowFaceWholeLifeEnabled: boolean = false;

  @property({ toClient: true, toServer: false })
  isHeavyUserEnabled: boolean = false;

  @property({ toClient: true, toServer: false })
  whichController: string = 'Client';

  @property({ toClient: true, toServer: false })
  customerFirstLastName: string;

  @property({ toServer: false })
  clientSiteAuditingEnabled: boolean = false;

  @property({ toServer: false })
  editAppEnabled: boolean = false;

  @property({ toClient: false })
  txn;

  @property()
  biHtmlStr: string;

  @property({ toServer: false, toClient: false })
  functionName: string;

  @property({ toServer: false })
  isBackDatingAmendmentEnabled: boolean = false;

  @property({ toServer: false })
  isDocuSignRedirectEnabled: boolean = false;

  @property({ toServer: false })
  earlyInsuredConsentEnabled: boolean = false;

  @property({ toServer: false })
  lowerDividendRateEnabled: boolean = false;

  @property({ toServer: false })
  inForceIllustrationEnabled: boolean = false;

  @property({ toServer: false })
  alirScheduled: boolean = false;

  @property({ toServer: false })
  alir1035NoPremium: boolean = false;

  @property({ toServer: false })
  alir1035Premium: boolean = false;

  @property({ toServer: false })
  cirEnabled: boolean = false;

  @property({ toServer: false })
  annuityEnabled: boolean = false;

  @property({ toServer: false, getType: () => PolicyDoc })
  policyDocuments: PolicyDoc[];

  @property({ toServer: false })
  isCoverpathNYEnabled: boolean = false;

  @property({ toServer: false })
  nonroundedFaceAmountsEnabled: boolean = false;

  @property({ toServer: false })
  cacCashValueEnabled: boolean = false;

  @property({ toClient: false, toServer: false, sensitiveData: true })
  amcGuid: string;

  @property({ toServer: false })
  isTeleCMIEnabled: boolean = false;

  @property({ toServer: false })
  mmGiactWebDebitVerificationServiceEnabled = {};

  @property({ toServer: false })
  isCoverpathClient: boolean = true;

  @property({ toServer: false })
  enableConversionAutomation: boolean = false;

  @property({ toServer: false })
  enableGenesysCobrowse: boolean = false;

  @property({ toServer: false })
  altSubmitSignVerbiageEnabled: boolean = false;

  @property({ toServer: false })
  isQuestionLockEnabled: boolean = false;

  @property({ toServer: false })
  quickQuoteForm?: QuoteForm;

  @property({ toServer: false })
  enableAuthPayforDDB: boolean = false;

  @property({ toServer: false })
  enableFailedPaymentRetry: boolean = false;

  @property({ toServer: false })
  lowFaceWL10EnableDate: string = '';

  @property({ toServer: false })
  docusignClientCancelPowerFormId: string = '';

  @property({ toServer: false })
  nbDecommissionPost30Days: boolean = false;

  @property({ toServer: false })
  cpClientCancelTermPolicyPowerFormId: string = '';

  @property({ toServer: false })
  cpClientChangeFaceAmountPowerFormId: string = '';

  @property({ toServer: false })
  cpClientChangeNamePowerFormId: string = '';

  @property({ toServer: false })
  cpClientChangeContactInfoPowerFormId: string = '';

  serverInit(): void {
    super.serverInit();

    // set UwAppCtx global logger so we don't error out
    try {
      const ctx = HitAppCtx.get;
      UwClientAppCtx.get.makeGlobal(LoggerSingleton.getInstance());
    } catch (e) {
      const err = {
        isHumanRelated: false,
        message: e?.message,
      };
      LoggerSingleton.getInstance().error({
        module: 'Controller:coverpath-client',
        function: 'serverInit',
        category: Category.availability,
        message: 'UwClientAppCtx.get.makeGlobal error',
        error: err,
      });
    }
    this.nonProdMode = !Utils.isProdEnv();

    this.emailer = this.emailer || HitAppCtx.get.emailer;

    if (!this.htmlPdfController) {
      this.htmlPdfController = HtmlPdfController.new();
    }

    if (!this.htmlPdfContentController) {
      this.htmlPdfContentController = HtmlPdfContentController.new();
    }

    this.submittedPolicyValidatingController =
      this.submittedPolicyValidatingController || SubmittedPolicyValidatingController.new();

    if (!this.sanderlingPolicyRepairController) {
      this.sanderlingPolicyRepairController = SanderlingPolicyRepairController.new(this);
    }

    //Use 2019 Filing for application questions
    this.use2019Filing = CoverpathUtils.isFeatureEnabled(FeatureFlags.use2019Filing);

    if (!this.remoteDocServiceController) {
      this.remoteDocServiceController = RemoteDocServiceController.new(this);
    }

    this.segmentKey = this.amorphic.config.nconf.get('segmentKey');
    this.sendToSegment = this.amorphic.config.nconf.get('sendToSegment');
  }

  getPrincipal(): AuthenticatedPrincipal {
    return this.customer;
  }

  setPrincipal(principal): void {
    this.customer = principal;
  }

  newPrincipal(): CoverpathCustomer {
    const customer = CoverpathCustomer.new('coverpathClientQuote');
    return customer;
  }

  refresh(): void {}

  /**
   * When the app container restarts, Amorphic resets the active session on the next remote call but the session
   * doesn't flow to the client causing semotus sync errors. Side effect is remote calls fail leading
   * to cases like - user clicking on the clients tab sees an eternal spinner or user filling an application
   * thinks that changes are persisted but they are not.
   * Amorphic provides the following callback function to let controllers do specific processing on
   * a reset, here we merely reload the page to get the session onto the client.
   */
  shutdown(): void {
    window.location.reload();
  }

  handleRemoteError(error) {
    // Handle framework errors only
    if (error && error.code) {
      if (error.code.match(/inactive_app/)) {
        window.location.href = `/concurrent-modification?type=${error.applicationType}`;
      } else if (error.code.match(/internal_error/)) {
        window.location.reload(true);
      }
    }
  }

  @serverOnly()
  async processPost(
    originalUrl: string,
    body: { path?: string },
    req: Request,
    res: Response
  ): Promise<{ status: number; body?: string; headers: { 'location': string; 'Content-Type': string } }> {
    if (body && body.path) {
      LoggerSingleton.getInstance().info({
        module: 'Controller',
        function: 'processPost',
        category: Category.request,
        context: {
          piiLevel: PiiLevelEnum.nonPII,
        },
        data: {
          path: body.path,
        },
      });

      if (body.path === '/fetchSession') {
        return this.fetchAMCAuthData();
      } else {
        return { status: 400, headers: { 'Content-Type': 'application/json', 'location': '' } };
      }
    }
  }

  @serverOnly()
  fetchAMCAuthData() {
    const data: Auth0Configs = Auth0Utils.getAuth0Configuration(Auth0Channel.COVERPATH);

    LoggerSingleton.getInstance().info(
      {
        module: 'Controller',
        function: 'fetchAMCAuthData',
        category: Category.request,
        context: {
          piiLevel: PiiLevelEnum.nonPII,
        },
      },
      'Fetching data from configs for AMC silent authentication'
    );

    const stringifiedConfig = JSON.stringify(data);

    return { status: 200, headers: { 'Content-Type': 'application/json', 'location': '' }, body: stringifiedConfig };
  }

  clientInit(): void {
    // Internet Explorer 6-11
    if (/MSIE|Trident/i.test(window.navigator.userAgent)) {
      document.location.href = '/unsupported_browser.html';

      UILoggerSingleton.getInstance().info(
        {
          module: 'controller',
          function: 'ClientInit',
          category: UICategory.security,
        },
        'Unsupported Browser, redirecting to /unsupported_browser.html'
      );
    }

    if (this.customer) {
      this.customer.clientInit();
    } else {
      this.customer = CoverpathCustomer.new('coverpathClientQuote');
    }

    if (!this.workflowHelper) {
      this.workflowHelper = WorkflowHelper.new();
    }

    if (!this.tfaController) {
      this.tfaController = TFAController.new();
    }

    if (!this.productRulesController) {
      this.productRulesController = new ProductRulesController();
    }
    this.productRulesController.loadRules(Product.channels.CAS.code);

    if (!this.emailNotificationController) {
      this.emailNotificationController = EmailNotificationController.new(this);
    }

    if (!this.accountCenterController) {
      this.accountCenterController = AccountCenterController.new(this);
    }

    if (!this.inviteController) {
      this.inviteController = InviteController.new(this);
    }

    if (!this.applicationController) {
      this.applicationController = ApplicationController.new();
    }

    if (!this.commissionSplitsController) {
      this.commissionSplitsController = CommissionSplitsController.new(this);
    }

    if (!this.productClientController) {
      this.productClientController = ProductClientController.new();
    }

    if (!this.applicationLockController) {
      this.applicationLockController = ApplicationLockController.new(this);
    }

    if (!this.giactController) {
      this.giactController = GiactController.new();
    }

    setInterval(() => {
      if (window.amorphic.state === 'zombie') {
        document.location.href = '/inactive_tab.html';

        UILoggerSingleton.getInstance().info(
          {
            module: 'Controller',
            function: 'clientInit',
            category: UICategory.security,
          },
          'Tab entered zombie mode, redirecting to /inactive_tab.html'
        );
      }
    }, 100);
  }

  getAMCController(): AMCController {
    if (!this.amcController) {
      this.amcController = AMCController.new(this);
    }
    return this.amcController;
  }

  public async getVSTData(quote?: QuoteForm, safeInviteInfo: SafeInviteInfo = null): Promise<VSTResult | null> {
    let vstRequest: IllustrationRequest;
    if (quote) {
      vstRequest = IllustrationRequestHelpers.createReactiveVSTRequest(quote);
    } else {
      const customer = this.customer as CoverpathCustomer;
      if (!!safeInviteInfo) {
        vstRequest = IllustrationRequestHelpers.createSafeVSTRequest(safeInviteInfo);
      } else {
        vstRequest = IllustrationRequestHelpers.createVSTRequest(customer);
      }
    }
    const data = await this.productClientController.getVSTData(vstRequest);
    if (data) {
      return IllustrationRequestHelpers.createVSTResult(data);
    }
  }

  @remote({ on: 'server' })
  async publicFetchMetaData() {
    if (this.customer.application && this.customer.application.primaryPolicy) {
      try {
        await this.customer.enrichApplication(this.customer.application._id);
      } catch (error) {
        LoggerSingleton.getInstance().error(
          {
            module: 'coverpath-client controller',
            function: 'publicFetchMetaData',
            category: Category.request,
            context: {
              piiLevel: PiiLevelEnum.nonPII,
              referenceType: 'application',
              referenceId: this.customer.application._id,
            },
            error: {
              message: error.message,
              name: error.name,
              stack: error.stack,
              isHumanRelated: false,
              data: {
                application_id: this.customer.application._id,
              },
            },
          },
          `Error calling CoverpathCustomer.enrichApplication for applicationId ${this.customer.application._id}`
        );
        return;
      }
    }

    try {
      await this.setDataForLocalProps();
    } catch (error) {
      LoggerSingleton.getInstance().error(
        {
          module: 'controller',
          function: 'publicFetchMetaData',
          category: Category.request,
          context: {
            piiLevel: PiiLevelEnum.nonPII,
          },
          error: {
            message: error.message,
            name: error.name,
            stack: error.stack,
            isHumanRelated: false,
          },
        },
        'Error in call to getDataForLocalProps'
      );
    }

    try {
      this.isLocked = await this.applicationLockController.isApplicationLocked(LockedByUserType.CUSTOMER);
      return this.getMetadataForPolicy(this.customer.application.primaryPolicy);
    } catch (error) {
      LoggerSingleton.getInstance().error(
        {
          module: 'controller',
          function: 'publicFetchMetaData',
          category: Category.request,
          context: {
            piiLevel: PiiLevelEnum.nonPII,
          },
          error: {
            message: error.message,
            name: error.name,
            stack: error.stack,
            isHumanRelated: false,
          },
        },
        'Error in call to getMetadataForPolicy'
      );
    }
  }

  async setDataForLocalProps() {
    const primaryPolicy = this.customer.application.primaryPolicy;
    const secondaryPolicy = this.customer.application.secondaryPolicy;

    if (primaryPolicy) {
      await ApplicationDataService.updatePolicyLocalProps(this, primaryPolicy);
    }
    if (secondaryPolicy) {
      await ApplicationDataService.updatePolicyLocalProps(this, secondaryPolicy);
    }
  }

  async getMetadataForPolicy(policy: Policy): Promise<any> {
    //if versionForPolicy is defined, we'll get the Metadata for that specific version
    //if it's undefined, we'll get the latest version

    const version = policy.appMetadataVersion?.toString() || undefined;
    const category = new MetadataCategory('App_Questions', version);
    const result = await MetadataApi.constructMetadata(category, policy.getApplicationHierarchy(), null);

    if (policy.appMetadataVersion === null || isNaN(policy.appMetadataVersion)) {
      UILoggerSingleton.getInstance().info(
        {
          module: 'controller',
          function: 'getMetadataForPolicy',
          category: UICategory.milestone,
          context: {
            referenceType: 'policy',
            referenceId: policy._id,
          },
        },
        `Stamping ${policy._id} with metadata version of ${result.version}`
      );

      policy.appMetadataVersion = parseFloat(result.version);
    }

    return result.metadata;
  }

  //Temporary fix to reassign the product null issue
  // This function has to be removed when the root cause issue is fixed for HL-42334
  async fixPolicyNullInfo(policy: CoverpathBasePolicy, isStateNull: boolean) {
    const appDataFromDB = await CasApplication.persistorFetchById(this.customer.application._id, {
      fetch: { customer: { fetch: { applications: false } } },
      session: this.amorphic,
    });

    let updatedPolicyInfo = {};
    const info = {
      product: null,
      state: null,
    };
    if (appDataFromDB) {
      // can't be used in both statements below so is unique
      if (!isStateNull) {
        if (appDataFromDB.primaryPolicy && policy.type === POLICY_TYPES.PRIMARY) {
          info.product = appDataFromDB.primaryPolicy.product;
        } else if (appDataFromDB.secondaryPolicy && policy.type === POLICY_TYPES.SECONDARY) {
          info.product = appDataFromDB.secondaryPolicy.product;
        }

        updatedPolicyInfo = {
          type: policy.type === POLICY_TYPES.PRIMARY ? POLICY_TYPES.PRIMARY : POLICY_TYPES.SECONDARY,
          product: policy.product,
          term: policy.term,
        };

        UILoggerSingleton.getInstance().info(
          {
            module: 'Controller',
            activity: 'fixPolicyNullInfo',
            category: UICategory.milestone,
            data: {
              updatedPolicy: updatedPolicyInfo,
            },
          },
          'HL-42334: Fixed Product being null'
        );
      } else {
        info.state = appDataFromDB.customer.application.primaryPolicy.insured.address.state;
        UILoggerSingleton.getInstance().info(
          {
            module: 'Controller',
            function: 'fixPolicyNullInfo',
            category: UICategory.milestone,
            data: {
              updatedStateInfo: info.state,
            },
          },
          'HL-49630: Fixed state information being null'
        );
      }
    }

    return info;
  }

  @remote({ on: 'server' })
  publicSave(): Promise<void> {
    // TODO https://massmutual.atlassian.net/browse/HL-40906
    // This is hacky and needs to be removed once the cascade delete functionality is provided by the framework.
    // Look at CoverpathBasePolicy.ts, set alirLumpSumUses1035() function
    if (this.customer && this.customer.application && this.customer.application.primaryPolicy) {
      const policy: CoverpathBasePolicy = this.customer.application.primaryPolicy;
      if (policy.hasAlirRider && !policy.alirLumpSumUses1035 && !!policy.alirRider.exchange1035) {
        this.customer.registerPostServerCallCallback(() => policy.alirRider.remove1035());
      }
    }
    return Promise.resolve();
  }

  @remote({ on: 'server' })
  publicGetLocationByZip(zipcode) {
    return CommonUtilsBackend.publicGetLocationByZip(zipcode);
  }

  @remote({ on: 'server' })
  public async publicFetchProductSpec(requestOptions: ProductSpecRequest): Promise<ProductSpecResponse> {
    LoggerSingleton.getInstance().info(
      {
        module: 'controller',
        function: 'publicFetchProductSpec',
        category: Category.request,
        context: {
          piiLevel: PiiLevelEnum.nonPII,
        },
        data: {
          productServicePayload: safeJsonStringify(requestOptions),
        },
      },
      'fetching a product spec from the product service'
    );

    const productSpec: ProductSpecResponse = await ProductServiceClient.getProductSpec(requestOptions);
    return productSpec;
  }

  @remote({ on: 'server' })
  public async publicFetchProductQuote(
    requestOptions: PremiumRequest,
    requestIdentifier?: string,
    version?: string
  ): Promise<ProductQuoteResponse> {
    LoggerSingleton.getInstance().info(
      {
        module: 'controller',
        function: 'publicFetchProductQuote',
        category: Category.request,
        context: {
          piiLevel: PiiLevelEnum.nonPII,
        },
        data: {
          productServicePayload: safeJsonStringify(requestOptions),
        },
      },
      'fetching a quote from the product service'
    );
    //const policy = this.customer.application.primaryPolicy;

    // don't have a policy number for a quote, use customer ID instead to track req through system.
    //TODO: Fetch premium version from policy via Safe Invite information instead of passing
    // NULL in getPremium
    const premium: PremiumResponse = await ProductServiceClient.getPremium(
      requestIdentifier || this.customer._id,
      version,
      requestOptions
    );
    const response = {
      base: Utils.round(Number(premium.base), 2).valueOf(),
      waiver: requestOptions.waiver ? Utils.round(Number(premium.waiver), 2).valueOf() : 0,
      riders: [],
      annualBaseRates: premium.annualBaseRates || [],
      annualWaiverRates: premium.annualWaiverRates || [],
      annualBase: Utils.round(Number(premium.annualBase), 2).valueOf(),
    };
    premium.riders.forEach((rider) => {
      rider.premium = Utils.round(Number(rider.premium), 2).valueOf();
      rider.waiver = Utils.round(Number(rider.waiver), 2).valueOf();
      response.riders.push(rider);
    });
    return response;
  }

  @remote({ on: 'server' })
  public async publicFetchMultipleProductQuotes(
    requestOptions: PremiumRequestList,
    productPremiumVersion?: string
  ): Promise<ProductQuoteResponseList> {
    LoggerSingleton.getInstance().info(
      {
        module: 'controller',
        function: 'publicFetchMultipleProductQuotes',
        category: Category.request,
        context: {
          piiLevel: PiiLevelEnum.nonPII,
        },
        data: {
          productServicePayload: safeJsonStringify(requestOptions),
        },
      },
      'fetching a quote from the product service'
    );

    // don't have a policy number for a quote, use customer ID instead to track req through system.
    const premiumList: PremiumResponseList = await ProductServiceClient.getMultiplePremiums(
      this.customer._id,
      productPremiumVersion,
      requestOptions
    );

    const productQuoteReponseList: ProductQuoteResponseList = {
      responses: [],
    };

    premiumList.responses.forEach((premium, i) => {
      const productQuoteReponse: ProductQuoteResponse = {
        base: Utils.round(Number(premium.base), 2).valueOf(),
        waiver: requestOptions.requests[i].waiver ? Utils.round(Number(premium.waiver), 2).valueOf() : 0,
        riders: premium.riders || [],
        flatExtras: premium.flatExtras || [],
        annualBaseRates: premium.annualBaseRates || [],
        annualWaiverRates: premium.annualWaiverRates || [],
        totalTargetPremium: premium.totalTargetPremium,
        annualBase: Utils.round(Number(premium.annualBase), 2).valueOf(),
      };

      productQuoteReponseList.responses.push(productQuoteReponse);
    });

    return productQuoteReponseList;
  }

  makePropsForNewCustomer(): CoverpathCustomerProps {
    const policy = {
      monthly: this.customer.primaryQuote.premium,
      waiverOfPremium: this.customer.primaryQuote.waiverOfPremiumAmount,
      carrier: 'MassMutual',
      face: this.customer.primaryQuote.faceAmount,
      term: this.customer.primaryQuote.term,
      product: this.customer.primaryQuote.product,
      carrierCode: 'CAS',
      rating: 'A++',
    };
    const quote = {
      product: this.customer.primaryQuote.product,
      productType: this.customer.primaryQuote.productType,
      faceAmount: this.customer.primaryQuote.faceAmount || DefaultQuote.faceAmount,
      term: this.customer.primaryQuote.term || DefaultQuote.term,
      smoker: this.customer.primaryQuote.smoker || DefaultQuote.smoker,
      dateOfBirth: this.customer.primaryQuote.dateOfBirth || DefaultQuote.dateOfBirth,
      premium: this.customer.primaryQuote.premium || DefaultQuote.premium,
      healthClass: this.customer.primaryQuote.healthClass || DefaultQuote.healthClass,
      contractState: this.customer.primaryQuote.contractState || DefaultQuote.contractState,
      waiverOfPremium: this.customer.primaryQuote.waiverOfPremium || DefaultQuote.waiverOfPremium,
      gender: this.customer.primaryQuote.gender || DefaultQuote.gender,
      waiverOfPremiumAmount: this.customer.primaryQuote.waiverOfPremiumAmount || DefaultQuote.waiverOfPremiumAmount,
      paymentFrequency: this.customer.primaryQuote.paymentFrequency || DefaultQuote.paymentFrequency,
      policies: [policy],
    };

    // Debugger for the ticket HL-42869
    if (quote.productType === null) {
      UILoggerSingleton.getInstance().info(
        {
          module: 'Controller',
          function: 'makePropsForNewCustomer',
          category: UICategory.request,
          context: {
            piiLevel: UIPiiLevelEnum.s2, // TODO: Verify
          },
          data: {
            productType: this.customer.primaryQuote,
          },
        },
        'HL-42869: ProductType null issue'
      );
    }

    const personAge = quote.dateOfBirth ? Utils.getAgeFromBirthday(quote.dateOfBirth.toLocaleDateString()) : null;
    return {
      quote: quote,
      quoteType: 'coverpathTermQuote',
      person: {
        email: this.email,
        firstName: this.firstName,
        lastName: this.lastName,
        age: personAge,
      },
      address: {
        state: this.customer.primaryQuote.contractState,
      },
      progress: 1,
      tfaRequired: this.customer.application.isAgentAssistant,
      primaryQuote: this.customer.primaryQuote,
    };
  }

  @remote({ on: 'server' })
  async register(url: string): Promise<boolean> {
    const authInformation: AuthenticationInformation = await this.publicControllerAuthenticate();
    await this.prepareSessionData(authInformation);
    return true;
  }

  protected async authenticatePrincipal(
    principal: PrincipalCustomer,
    isExistingAccount: boolean,
    password: string
  ): Promise<void> {
    const ref: string | null = principal ? principal._id : null;
    LoggerSingleton.getInstance().info(
      {
        module: 'Controller',
        function: 'authenticatePrincipal',
        category: Category.milestone,
        context: {
          piiLevel: PiiLevelEnum.s2,
          referenceType: 'PrincipalCustomer',
          referenceId: ref,
        },
        data: {
          email: this.email,
          principal: ref,
          isExistingAccount: isExistingAccount,
        },
      },
      'Auth0 authenticatePrincipal'
    );

    if (isExistingAccount) {
      principal = await this.getPrincipal().amorphicClass.persistorFetchById(principal._id);

      if (!this.isAuth0Enabled) {
        if (AuthOwner.AUTH0 === principal.authOwner) {
          LoggerSingleton.getInstance().info(
            {
              module: 'Controller',
              function: 'authenticatePrincipal',
              category: Category.milestone,
              context: {
                piiLevel: PiiLevelEnum.s2,
                referenceType: 'PrincipalCustomer',
                referenceId: principal._id,
              },
              data: {
                email: principal.email,
              },
            },
            'Recognizing legacy auth method with an Auth0 migrated account. Authenticating with Auth0'
          );

          try {
            await Auth0Client.authenticate(principal.email, password);
            await principal.establishPassword(password, null, true, false);
          } catch (error) {
            if (error && error.error && error.error.error_description) {
              throw {
                code: error.error.error_description,
                text: error.error.error_description,
              };
            } else {
              throw {
                code: 'AUTH0_AUTHENTICATE_UNKNOWN_FAILURE',
                text: `There was an issue authenticating your account.  Please contact support.`,
              };
            }
          }
        } else {
          LoggerSingleton.getInstance().info(
            {
              module: 'Controller',
              function: 'authenticatePrincipal',
              category: Category.milestone,
              context: {
                piiLevel: PiiLevelEnum.s2,
                referenceType: 'PrincipalCustomer',
                referenceId: principal._id,
              },
              data: {
                email: principal.email,
                isExistingAccount: isExistingAccount,
              },
            },
            'Recognizing legacy auth method. Performing traditional authentication'
          );

          const status: string = await this.getCustomerStatusForEmail(principal.email);

          if (status && status === 'exists:notregistered') {
            await principal.setEmailVerificationCode();
            await principal.establishPassword(this.newPassword, null, true, false);
            principal.emailValidated = true;
            await principal.persistorSave();
          } else {
            await principal.authenticate(password, null, null);
          }

          this.amorphicate(principal);
        }
      } else {
        if (this.auth0IdToken) {
          this.amorphicate(principal);
        } else {
          LoggerSingleton.getInstance().warn(
            {
              module: 'Controller',
              function: 'authenticatePrincipal',
              category: Category.milestone,
              context: {
                piiLevel: PiiLevelEnum.s2,
                referenceType: 'PrincipalCustomer',
                referenceId: principal._id,
              },
              data: {
                email: principal.email,
                isExistingAccount: isExistingAccount,
              },
            },
            'Attempted to log in without Auth0 ID Token'
          );

          throw {
            code: 'invalid_authentication',
            text: 'Login attempt without valid ID Token',
          };
        }
      }
    }

    this.setLoggedInState(principal);

    LoggerSingleton.getInstance().info(
      {
        module: 'Controller',
        function: 'authenticatePrincipal',
        category: Category.milestone,
        context: {
          piiLevel: PiiLevelEnum.s2,
          referenceType: 'PrincipalCustomer',
          referenceId: ref,
        },
        data: {
          email: this.email,
          principal: principal ? principal._id : null,
          role: principal ? principal.role : null,
        },
      },
      'Finished setting session log in state'
    );
  }

  async getCustomerStatusForEmail(email: string): Promise<string> {
    if (!email) {
      return 'nonexistent';
    }

    try {
      const results = await CoverpathCustomer.persistorFetchByQuery(
        { email: email, channel: Product.channels.CAS.code },
        { session: this.amorphic }
      );

      if (results && results[0] && results[0].hasRegistered) {
        return 'exists:registered';
      } else if (results && results[0]) {
        return 'exists:notregistered';
      } else {
        return 'nonexistent';
      }
    } catch (error) {
      UILoggerSingleton.getInstance().error(
        {
          module: 'Controller',
          function: 'getCustomerStatusForEmail',
          category: UICategory.request,
          error: {
            message: error.message,
            stack: error.stack,
            name: error.name,
            isHumanRelated: false,
          },
        },
        'Error in getCustomerStatusForEmail'
      );

      throw error;
    }
  }

  cpFinalizeRegistration(): void {
    this.loggedIn = true;
    this.customer.clientInit();
    this.clearPasswords();
    this.customer.advanceApplicationProgress(12);
    this.customer.hasRegistered = true;
  }

  @remote({ on: 'server' })
  login(loginType?: LoginType): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.loginByType(loginType)
        .then(() => this.fetchAccessories())
        .then(() => this.postProcessing(loginType))
        .then(async () => {
          this.loggedIn = true;
          this.customer.clientInit();
          this.clearPasswords();
          this.customer.advanceApplicationProgress(12);
          await this.initializeFromQuickQuote();
          resolve(true);
        })
        .then(() => {
          this.identifyToSegment(this.customer._id, {
            user_type: 'customer',
            user_id: this.customer._id,
          });
        })
        .catch((error) => {
          LoggerSingleton.getInstance().error(
            {
              module: 'controller',
              function: 'login',
              category: Category.request,
              context: {
                piiLevel: PiiLevelEnum.nonPII,
              },
              error: {
                message: error.message,
                name: error.name,
                stack: error.stack,
                isHumanRelated: false,
                data: {
                  text: Utils.toString(error.text),
                },
              },
            },
            'Error in login'
          );
          reject(false);
        });
    });
  }

  protected async identifyPrincipal(
    email: string,
    firstName: string,
    lastName: string,
    policyNumber?: string,
    applicationNumber?: string
  ): Promise<IdentifiedPrincipal> {
    const loggerData = {
      module: 'Controller',
      function: 'identifyPrincipal',
      category: UICategory.milestone,
      context: {
        piiLevel: UIPiiLevelEnum.s2,
      },
      data: {
        email: email,
        isAuth0Enabled: this.isAuth0Enabled,
        customerAccount: undefined,
        customer: undefined,
        identifiedByCustomerId: undefined,
        hasPrincipal: undefined,
        isSuspended: undefined,
        identifiedPrincipal: undefined,
        isExistingUser: undefined,
      },
    };
    UILoggerSingleton.getInstance().info(loggerData, 'Identifying Principal');

    let isExistingCustomerObject: boolean = true;

    const customerAccount: Account = await this.fetchCustomerAccount();
    // set customer account id for logging
    loggerData.data.customerAccount = customerAccount ? customerAccount._id : undefined;

    if (customerAccount && this.isAuth0Enabled) {
      const authorizedAccount: AccountAuthorization = customerAccount.accountAuthorizations.find((accountAuth) => {
        return accountAuth.managedEntityType.templateName === CoverpathCustomer.amorphicClassName;
      });

      let customer: PrincipalCustomer;
      if (authorizedAccount) {
        UILoggerSingleton.getInstance().info(loggerData, 'Authorized Account found; loading principal');
        customer = await Customer.persistorFetchById(authorizedAccount.managedEntityId);
      } else {
        UILoggerSingleton.getInstance().info(loggerData, 'No Authorized Account found; Creating a new principal...');
        customer = await this.createUser(email, firstName, lastName);
        isExistingCustomerObject = false;
      }

      // set the customer id once found
      loggerData.data.customer = customer ? customer._id : undefined;

      UILoggerSingleton.getInstance().info(loggerData, 'Identified an existing customer account');

      return {
        principal: customer,
        isExistingUser: isExistingCustomerObject,
      };
    }
    // -----------------------------------------
    else {
      UILoggerSingleton.getInstance().info(loggerData, 'No Customer Account when Identifying Principal');

      let principal;
      if (this.managedEntityId) {
        principal = await Customer.persistorFetchById(this.managedEntityId);
      }

      loggerData.data.identifiedByCustomerId = !!principal;
      loggerData.data.customer = this.managedEntityId;
      UILoggerSingleton.getInstance().info(loggerData, 'Results for identifying principal by customerId');

      try {
        if (!principal) {
          const principals = await this.fetchPrincipalQuery(email);
          if (principals.length === 0) {
            principal = await this.createUser(email, firstName, lastName);
            isExistingCustomerObject = false;
            UILoggerSingleton.getInstance().info(
              {
                module: 'Controller',
                function: 'identifyPrincipal',
                category: UICategory.milestone,
                context: {
                  piiLevel: UIPiiLevelEnum.s2,
                },
                data: {
                  email,
                  firstName,
                  lastName,
                  isExistingCustomerObject,
                },
              },
              'None found -> Create a new account'
            );
          } else {
            principal = principals[0];
            isExistingCustomerObject = true;
            UILoggerSingleton.getInstance().info(
              {
                module: 'Controller',
                function: 'identifyPrincipal',
                category: UICategory.milestone,
                context: {
                  piiLevel: UIPiiLevelEnum.s2,
                },
                data: {
                  email,
                  firstName,
                  lastName,
                  isExistingCustomerObject,
                },
              },
              'Match found -> Use existing account'
            );
          }
        }
      } catch (e) {
        UILoggerSingleton.getInstance().error(
          {
            module: 'Controller',
            function: 'identifyPrincipal',
            category: UICategory.milestone,
            context: {
              piiLevel: UIPiiLevelEnum.s2,
            },
            error: {
              message: e.message,
              name: e.name,
              stack: e.stack,
              isHumanRelated: false,
              data: {
                email,
                firstName,
                lastName,
                isExistingCustomerObject,
              },
            },
          },
          'Unable to find or create Principal'
        );
      }

      // Suspended or Non-Existent
      if (!principal) {
        loggerData.data.hasPrincipal = !!principal;
        UILoggerSingleton.getInstance().warn(loggerData, 'Log In attempt failed.  Unable to identify user');

        throw {
          code: 'invalid_user',
          text: 'Incorrect email or password',
        };
      }

      if (principal.suspended) {
        loggerData.data.isSuspended = true;
        UILoggerSingleton.getInstance().warn(loggerData, 'Log In attempt failed (user suspended)');

        throw {
          code: 'invalid_user_suspended',
          text: 'User Suspended',
        };
      }

      loggerData.data.isExistingUser = isExistingCustomerObject;
      loggerData.data.identifiedPrincipal = principal._id;
      UILoggerSingleton.getInstance().info(loggerData, 'Identified principal');

      return {
        principal: principal,
        isExistingUser: isExistingCustomerObject,
      };
    }
  }

  protected async createUser(email: string, firstName: string, lastName: string): Promise<PrincipalCustomer> {
    this.setPrincipal(this.getPrincipal() || this.newPrincipal());
    const principal = this.getPrincipal();
    principal.email = email;
    principal.firstName = firstName;
    principal.lastName = lastName;

    if (!this.isAuth0Enabled) {
      this.customer.previousSalts = [];
      this.customer.previousHashes = [];

      await this.customer.setEmailVerificationCode();
      await this.customer.establishPassword(this.newPassword, null, true, false);
    }

    await AuthenticationHelpers.insertFilter.call(this, principal);

    return principal;
  }

  @remote({ on: 'server' })
  async resetPassword(url: string): Promise<void> {
    if (this.isAuth0Enabled) {
      await this.getAMCController().updateAMCPassword();
      this.clearPasswords();
    } else {
      return this.publicResetPassword(url, this.customer.channel);
    }
  }

  @remote({ on: 'server' })
  finishResetPassword(): Promise<boolean> {
    return this.login(LoginType.fromToken);
  }

  protected clearPasswords(): void {
    this.newPassword = '';
    this.confirmPassword = '';
    this.password = '';
  }

  @remote({ on: 'server' })
  loginByType(loginType?: LoginType): Promise<void> {
    if (!this.getPrincipal()) {
      this.resetCustomer();
    }
    switch (Number(loginType)) {
      case LoginType.withNewEmail:
        return this.publicLoginWithNewEmail();

      case LoginType.fromToken:
        return this.publicChangePasswordFromToken();

      default:
        return this.publicLogin(true);
    }
  }

  public async logout(): Promise<void> {
    await this.serverLogout();
    this.clearPasswords();
  }

  @remote({ on: 'server' })
  postProcessing(loginType: LoginType | void): void {
    if (Number(loginType) === LoginType.withNewEmail) {
      const policy: Policy = this.customer.application.primaryPolicy;
      if (policy.owner) {
        policy.owner.email = this.customer.email;
      }
      if (policy.isSelfInsuredAndSelfOwner() && policy.insured && policy.insured.person) {
        policy.insured.person.email = this.customer.email;
      }
    }
  }

  /**
   * This function is only used during the login/register process of coverpath-client
   */
  async fetchAccessories(): Promise<void> {
    LoggerSingleton.getInstance().info(
      {
        module: 'controller.publicControllerLogin',
        function: 'fetchAccessories',
        category: UICategory.request,
        context: {
          piiLevel: UIPiiLevelEnum.s2,
          referenceType: 'Customer',
          referenceId: this.customer._id,
        },
      },
      'Login: ' + this.customer.email
    );

    this.amorphic.reqSession.loggingID = this.customer.email;

    try {
      await this.customer.enrichCustomer();
      // we need to re-set the currentApplication to the latest active applications ignoring any drafts in progress
      const mostRecentNonDraftApp = this.customer.getLatestApplication(true);
      this.customer.setCurrentApplication(mostRecentNonDraftApp._id);
      await this.customer.application.retrieveApplicant();

      // Debugger for the ticket HL-42869
      if (this.customer.application.quote.productType === null) {
        UILoggerSingleton.getInstance().info(
          {
            module: 'Controller',
            function: 'fetchAccessories',
            category: UICategory.request,
            context: {
              piiLevel: UIPiiLevelEnum.s2, //TODO: check
              referenceType: 'Quote',
              referenceId: this.customer.application.quote._id,
            },
            data: {
              productType: this.customer.application.quote._id,
            },
          },
          'HL-42869: ProductType null issue'
        );
      }
    } catch (error) {
      UILoggerSingleton.getInstance().error(
        {
          module: 'controller.publicControllerLogin',
          function: 'fetchAccessories',
          category: UICategory.milestone,
          context: {
            piiLevel: UIPiiLevelEnum.s2,
            referenceType: 'Customer',
            referenceId: this.customer._id,
          },
          error: {
            message: error.message,
            name: error.name,
            stack: error.stack,
            isHumanRelated: false,
          },
        },
        'Error in Login: ' + this.customer.email
      );
    }
  }

  resetCustomer(): void {
    this.customer = CoverpathCustomer.new('coverpathClientQuote');
  }

  @remote({ on: 'server' })
  public async serverLogout(): Promise<void> {
    await this.publicLogout();
    await this.resetCustomer();
  }

  /**
   * ensuring customer reset happens on both client and server
   *
   * Usecase: Need a way to get rid of customer info if user open advisor link, and finds out that application has been deleted.
   *
   * Use of publicLogout leads to errors on quote section, so It cannot be used
   * Remove once HL-14363 is complete
   */
  @remote({ on: 'server' })
  hardResetCustomer(): Promise<void> {
    this.customer = CoverpathCustomer.new('coverpathClientQuote');
    return Promise.resolve();
  }

  @remote({ on: 'server' })
  checkTFARequired(): boolean {
    if (this.customer && this.customer.tfaRequired && !this.customer.tfaAuthenticated) {
      LoggerSingleton.getInstance().warn(
        {
          module: 'Controller',
          function: 'checkTFARequired',
          category: Category.request,
          context: {
            piiLevel: PiiLevelEnum.s2,
          },
        },
        `Account requires TFA but is not TFA authenticated: ${this.email}`
      );

      throw {
        code: 'tfa_required',
        text: 'Please verify your account via Two Factor Authentication first.',
      };
    } else {
      return false;
    }
  }

  @remote({ on: 'server' })
  publicCheckEmailExists(): Promise<boolean> {
    if (!this.email) {
      throw { code: 'invalid email', text: 'email is null or empty' };
    }

    return CoverpathCustomer.persistorFetchByQuery({
      email: this.email,
      channel: Product.channels.CAS.code,
    }).then((results) => {
      if (results && results[0] && results[0].passwordHash) {
        LoggerSingleton.getInstance().info(
          {
            module: 'Controller',
            function: 'publicCheckEmailExists',
            category: Category.request,
            context: {
              piiLevel: PiiLevelEnum.nonPII,
            },
          },
          `Account already exists`
        );

        throw {
          code: 'account_exists',
          text: 'An account already exists for this email. Please sign in or use another email address.',
        };
      }
    });
  }

  sendEmail(template: string, email: string, name: string, vars): void {
    const params: EmailParams = _.reduce(
      vars,
      (obj, elem) => {
        obj[elem.name] = elem.content;
        return obj;
      },
      {}
    );

    const slugs = {
      register_verify: 'notify-registration-welcome',
      email_changed: 'notify-old-email-changed',
      email_changed_verify: 'verify-email-changed',
      password_reset: 'notify-password-reset',
      password_changed: 'confirmation-password-changed',
      confirm_emailchange: 'confirmation-email-changed',
      email_verify: 'notify-validation-code',
    };

    this.emailer.sendEmail({
      slug: slugs[template],
      recipientEmail: email,
      recipientName: name,
      confirmLink: params.link,
      confirmPin: params.verificationCode,
      resetLink: params.link,
      helpEmail: Product.channels[this.customer.channel].helpEmail,
      channel: this.customer.channel,
      productName: 'Term',
    });
  }

  /** Intercepts the request for a static resource, for example a pdf
   *  This is called even if the user is not logged in.
   */
  onContentRequest(request: Request, response: Response): void {
    const query = Url.parse(request.url, true).query;

    if (this.accountCenterController.isAccountCenterQueryString(query)) {
      this.accountCenterController.onContentRequestHandler(query, request, response);
    } else if (this.isInsuredEsignQueryString(query)) {
      this.onContentRequestHandler(query, request, response);
    } else if (this.isAnnuityQuery(query)) {
      // if the user is not logged in and is requesting a policy doc through a url
      this.loadAnnuityPdfForEsign(query, request, response);
    } else {
      response.writeHead(302, { Location: '/account-center' });
      response.end();
    }
  }

  private isAnnuityQuery(query): boolean {
    return query.annuity;
  }

  private async loadAnnuityPdfForEsign(query, request: Request, response: Response) {
    const { jwtToken, appType } = query;
    const { polno } = await this.parseAnnuitytoken(jwtToken);
    const policies = await Policy.persistorFetchByQuery({ policyNumber: polno });
    const policy = policies[0] as Policy;
    const policyDoc = await this.accountCenterController.getSinglePolicyDoc(policy, appType, appType);
    let docAsBuffer: Buffer, fileName: string;
    fileName = `${policyDoc.name}-${appType}`;
    docAsBuffer = Buffer.from(policyDoc.docContent.toString(), 'base64');

    response.writeHead(200, {
      'Content-Type': 'application/pdf',
      'Content-Disposition': `inline; filename=${fileName}`,
      'Content-Length': docAsBuffer.length,
    });

    const bufferStream = new stream.PassThrough();
    bufferStream.end(docAsBuffer);
    bufferStream.pipe(response);
  }

  private isInsuredEsignQueryString(query): boolean {
    return query.viewInsuredAmendmentsForm || query.viewInsuredInsurabilityForm || query.viewApplicationPdf;
  }

  getViewInsuredAmendmentsFormURL(): string {
    return `amorphic/xhr?path=${this.getPath()}&file=true&viewInsuredAmendmentsForm=true`;
  }

  getViewInsuredInsurabilityFormURL(): string {
    return `amorphic/xhr?path=${this.getPath()}&file=true&viewInsuredInsurabilityForm=true`;
  }

  getApplicationPdfURL(download: boolean): string {
    return `amorphic/xhr?path=${this.getPath()}&file=true&viewApplicationPdf=true&download=${download}`;
  }

  async onContentRequestHandler(query, request: Request, response: Response) {
    let docAsBuffer, fileName, policyDoc;
    if (query.viewInsuredInsurabilityForm) {
      policyDoc = this.policyDocuments.find(
        (policyDoc) =>
          [
            'ISDTC_DE_ND_SD_Insurability_Statement',
            'ISDTC_FL_Insurability_Statement',
            'ISDTC_CA_Insurability_Statement',
            'ICC17ISDTC_Insurability_Statement',
          ].includes(policyDoc.name) && policyDoc.isActive
      );
      fileName = `insurability-form`;
    } else if (query.viewInsuredAmendmentsForm) {
      policyDoc = this.policyDocuments.find(
        (policyDoc) =>
          ['AADTC-GEN', 'AADTC-FL', 'AADTC-CA', 'ICC17AADTC'].includes(policyDoc.name) && policyDoc.isActive
      );
      fileName = `amendment-form`;
    } else if (query.viewApplicationPdf) {
      fileName = 'Application';
      policyDoc = this.policyDocuments.find(
        (policyDoc) => policyDoc.name === 'ConsentedApplication' && policyDoc.isActive
      );
      if (query.download === 'true') {
        await CommonUtilsBackend.downloadFile(response, policyDoc as StreamableDoc, false, false);
        return;
      }
    }

    if (!policyDoc) {
      UILoggerSingleton.getInstance().warn(
        {
          module: 'Controller',
          function: 'onContentRequestHandler',
          category: UICategory.request,
          context: {
            piiLevel: UIPiiLevelEnum.nonPII,
          },
        },
        `Cannot find document for insured`
      );

      response.writeHead(404, {
        'Content-Type': 'text/plain',
      });
      response.end('Document not found');

      return Promise.resolve();
    }

    docAsBuffer = Buffer.from(policyDoc.docContent.toString(), 'base64');

    response.writeHead(200, {
      'Content-Type': 'application/pdf',
      'Content-Disposition': `inline; filename=${fileName}`,
      'Content-Length': docAsBuffer.length,
    });

    const bufferStream = new stream.PassThrough();
    bufferStream.end(docAsBuffer);
    bufferStream.pipe(response);
  }

  private setMostRecentNonDraftActiveAppAsCurrentApplication(): void {
    const mostRecentNonDraftActiveApp = this.customer.getLatestApplication(true);
    if (mostRecentNonDraftActiveApp) {
      this.customer.setCurrentApplication(mostRecentNonDraftActiveApp._id);
    } else {
      this.customer.setCurrentApplication();
    }
  }

  async preServerCall(
    hasChanges: boolean,
    _changes: PreServerCallChanges,
    _context: CallContext,
    forceUpdate: undefined | boolean,
    functionName: string,
    remoteCall: RemoteCall,
    isPublic: boolean,
    HTTPObjs: HTTPObjs
  ): Promise<void> {
    super.preServerCall(hasChanges, _changes, _context, forceUpdate, functionName, remoteCall, isPublic, HTTPObjs);
    await this.setFeatureFlags();

    function isStale() {
      if (this.loggedIn) {
        if (forceUpdate) {
          return true;
        }
        const isPrimaryPolicyStale =
          this.customer &&
          this.customer.application &&
          this.customer.application.primaryPolicy &&
          this.customer.application.primaryPolicy._id &&
          this.customer.application.primaryPolicy.__version__ &&
          this.customer.application.primaryPolicy.isStale();
        const isSecondaryPolicyStale =
          this.customer &&
          this.customer.application &&
          this.customer.application.secondaryPolicy &&
          this.customer.application.secondaryPolicy._id &&
          this.customer.application.secondaryPolicy.__version__ &&
          this.customer.application.secondaryPolicy.isStale();
        const isApplicationStale =
          this.customer &&
          this.customer.application &&
          this.customer.application._id &&
          this.customer.application.__version__ &&
          this.customer.application.isStale();
        const isCustomerStale =
          this.customer && this.customer._id && this.customer.__version__ && this.customer.isStale();
        return Promise.all([isPrimaryPolicyStale, isSecondaryPolicyStale, isApplicationStale, isCustomerStale]).then(
          (values) => {
            return values.some((val) => !!val);
          }
        );
      }
      return false;
    }

    function refreshCustomer(isStale) {
      if (this.customer && isStale) {
        UILoggerSingleton.getInstance().info(
          {
            module: 'Controller.preServerCall',
            function: 'refreshCustomer',
            category: UICategory.request,
            context: {
              piiLevel: UIPiiLevelEnum.nonPII,
            },
          },
          `Refreshing customer`
        );
      }

      return isStale ? refresh.call(this) : true;
    }

    function refresh() {
      return this.customer.refresh().then(async () => {
        const application = this.customer && this.customer.application;
        await this.customer.enrichCustomer();

        if (application) {
          // If there is already a current application, reload it from DB.
          await this.customer.enrichApplication(this.customer.application._id);
          // If the application is not active anymore, navigate away
          if (this.customer.application.type !== ApplicationTypes.ACTIVE) {
            const applicationType: string = this.customer.application.type;
            this.setMostRecentNonDraftActiveAppAsCurrentApplication();
            throw { code: 'inactive_app', applicationType: `${applicationType}` };
          }
        } else {
          this.setMostRecentNonDraftActiveAppAsCurrentApplication();
        }
      });
    }

    const dataForLog = { status: this.amorphic.__changeTracking__, forceUpdate: forceUpdate } as PreServerDataLog;

    if (this.customer) {
      dataForLog.version = this.customer.__version__;
      dataForLog.hasChanges = hasChanges;
      dataForLog.customerId = this.customer._id;

      this.customer.clearPostServerCallCallbacks();
    }
    UILoggerSingleton.getInstance().info(
      {
        module: 'controller',
        function: 'preServerCall',
        category: UICategory.request,
        context: {
          piiLevel: UIPiiLevelEnum.nonPII,
        },
        data: dataForLog,
      },
      `refresh complete`
    );

    if (this.nbDecommissionPost30Days && remoteCall.name === 'submitPolicy') {
      LoggerSingleton.getInstance().error(
        {
          module: 'Controller',
          function: 'preServerCall',
          category: Category.request,
          context: { piiLevel: PiiLevelEnum.nonPII },
          data: dataForLog,
          functionName: remoteCall.name,
        },
        'Internal Error - restricted function'
      );
      throw new Error('Internal Error - restricted function');
    }

    this.txn = this.amorphic.begin();

    return Promise.resolve().then(isStale.bind(this)).then(refreshCustomer.bind(this));
  }

  public checkNullInfo(changeString: { [key: string]: string }) {
    const result = {
      product: false,
      state: false,
    };
    if (changeString['CoverpathPolicy.product'] === '' || changeString['CoverpathPolicy.product'] === null) {
      result.product = true;
    }
    if (
      _.get(this, 'customer.application.primaryPolicy.productType', false) !== 'Annuity' &&
      (_.get(this, 'customer.application.primaryPolicy.insured.address.state', false) === '' ||
        _.get(this, 'customer.application.primaryPolicy.insured.address.state', false) === null)
    ) {
      result.state = true;
    }
    return result;
  }

  async postServerCall(
    hasChanges: boolean,
    callContext: { retries: number; startTime: Date },
    changeString: { [key: string]: string }
  ) {
    // semotus passes in (remoteCall.changes.length > 2, callContext, this.changeString)
    // when changes exist, we check for changed properties
    // we log the names of the properties to avoid exposing PII
    if (hasChanges) {
      const changedProperties = Object.keys(changeString);
      if (changedProperties.length > 0) {
        const hasNull = this.checkNullInfo(changeString);
        if (hasNull.product === true) {
          if (this.customer && this.customer.application && this.customer.application.primaryPolicy) {
            const policy = this.customer.application.primaryPolicy;
            if (policy.product === '' || policy.product === null) {
              this.customer.application.primaryPolicy.product = (
                await this.fixPolicyNullInfo(policy, hasNull.state)
              ).product;
            }

            const secondaryPolicy: CoverpathBasePolicy = this.customer.application.secondaryPolicy;
            if (secondaryPolicy && (secondaryPolicy.product === '' || secondaryPolicy.product === null)) {
              this.customer.application.secondaryPolicy.product = (
                await this.fixPolicyNullInfo(secondaryPolicy, hasNull.state)
              ).product;
            }
          }
          _.unset(changeString, 'CoverpathPolicy.product');
          _.unset(changeString, 'CoverpathPolicy.term');
        }
        if (hasNull.state === true) {
          if (this.customer && this.customer.application && this.customer.application.primaryPolicy) {
            const policy = this.customer.application.primaryPolicy;
            this.customer.application.primaryPolicy.insured.address.state = (
              await this.fixPolicyNullInfo(policy, hasNull.state)
            ).state;
          }
          _.unset(changeString, 'Address.state');
        }
        UILoggerSingleton.getInstance().info(
          {
            module: 'Controller',
            function: 'PostServerCall',
            category: UICategory.request,
            context: {
              piiLevel: UIPiiLevelEnum.nonPII,
            },
            data: {
              properties: changedProperties,
            },
          },
          `Saving properties to DB`
        );
      }
    }

    try {
      if (this.loggedIn && this.customer && this.customer.application && this.customer.application.primaryPolicy) {
        super.elasticPut(this.customer.application.primaryPolicy, false, true);
      }
      if (this.loggedIn) {
        this.customer.cascadeSave(this.txn);

        if (this.clientSiteAuditingEnabled) {
          this.txn.postSave = (txn, logger, changes) => {
            if (!changes || !CoverpathUtilsBackEnd.confirmChangesOccurred(changes)) {
              return;
            }
            const functionName = this.functionName;
            delete this.functionName;
            return AuditEvent.new(
              functionName, // always null for now
              null, // agent
              null, // agent proxy
              this.customer,
              changes
            ).save(this.txn);
          };
        }
      }
      await this.amorphic.commit({ transaction: this.txn, notifyChanges: true });

      if (this.customer) {
        await this.customer.notifyPostServerCallCallbacks();
        // customers can diverge becausee of quotes and the invite controller
        if (
          this.customer.application &&
          this.customer.application.primaryPolicy &&
          this.customer.application.primaryPolicy.customer !== this.customer
        ) {
          await this.customer.application.primaryPolicy.customer.notifyPostServerCallCallbacks();
        }
      }
    } finally {
      this.txn = null;
    }
  }

  /**
   * Hook called between pre and post server call to validate changes going from
   * client to server.
   * @param changes
   */
  validateServerIncomingObject(changes: Supertype): void {
    this.submittedPolicyValidatingController.validateServerIncomingObject(this.customer, changes);
  }

  @remote({ on: 'server' })
  publicCustomerPersistRefresh() {
    return this.customer.persistorRefresh();
  }

  @remote({ on: 'server' })
  async verifyEmail(): Promise<boolean> {
    const testMode =
      this.amorphic.config.nconf.get('environment') !== 'production'
        ? this.amorphic.config.nconf.get('test') || ''
        : '';
    if (testMode.match(/noverify/) && this.verifyEmailCode === '1234') {
      this.getPrincipal().emailValidated = true;
      return true;
    } else {
      try {
        await this.publicVerifyEmailFromCode();
        return true;
      } catch (error) {
        if (_.get(error, 'code') === AuthenticatedPrincipal.INVALID_VALIDATION_LINK) {
          LoggerSingleton.getInstance().error(
            {
              module: 'Controller',
              function: 'verifyEmail',
              category: Category.request,
              context: {
                piiLevel: PiiLevelEnum.nonPII,
              },
              error: {
                message: error.message,
                name: error.name,
                stack: error.stack,
                isHumanRelated: true,
              },
            },
            `Failed email verification due to ${AuthenticatedPrincipal.INVALID_VALIDATION_LINK}; user likely entered wrong code`
          );
          throw error;
        } else {
          LoggerSingleton.getInstance().error(
            {
              module: 'Controller',
              function: 'verifyEmail',
              category: Category.request,
              context: {
                piiLevel: PiiLevelEnum.nonPII,
              },
              error: {
                message: error.message,
                name: error.name,
                stack: error.stack,
                isHumanRelated: false,
              },
            },
            `Failed email verification`
          );
          throw error;
        }
      }
    }
  }

  @remote({ on: 'server' })
  async resetEmail(url: string): Promise<void> {
    if (this.isAuth0Enabled) {
      if (!this.newEmail) {
        throw { code: 'invalid email', text: 'Email is null or empty' };
      }
      const customer = await CoverpathCustomerUtils.getCustomerByEmailCaseInsensitive(this.newEmail);
      if (customer) {
        UILoggerSingleton.getInstance().error(
          {
            module: 'coverpathClient Controller',
            function: 'checkIfCustomerExistsWithEmail',
            category: UICategory.request,
          },
          'Existing customer found'
        );
        throw { code: 'email_registered', text: 'This email is already registered' };
      } else {
        await this.getAMCController().updateAMCEmail();
      }
    } else {
      await this.changeEmail(undefined, url);
      this.clearPasswords();
    }
  }

  reset(): void {}

  //TODO - Get working for multiple applications - https://massmutual.atlassian.net/browse/HL-19070
  @remote({ on: 'server' })
  public async updatePolicyInfoInPolicyAdmin(path: string, body, useSecondary?: boolean): Promise<void> {
    let policy = this.customer.application.primaryPolicy;
    if (useSecondary) {
      policy = this.customer.application.secondaryPolicy;
    }
    const policyAdminStatus = await PolicyAdminUtils.getPolicyAdminStatus(policy);
    return PolicyAdminUtils.updatePolicyInfoInPolicyAdmin(
      path,
      policy,
      body,
      policyAdminStatus.getPolicyAdminPolicyId()
    );
  }

  async setFeatureFlags() {
    const startTime = process.hrtime();
    const ldInstance = await LaunchDarklyInstance.getInstance(LD_APP.cp);
    if (ldInstance) {
      if (
        this.customer &&
        this.customer.application &&
        this.customer.application.primaryPolicy &&
        this.customer.application.primaryPolicy.primaryPolicyAgent
      ) {
        const producerId = this.customer.application.primaryPolicy.primaryPolicyAgent.primaryAgent.producerId;

        this.alirScheduled = await ldInstance.getFeatureFlagValueAsBooleanWithId(
          FeatureFlags.alirScheduled,
          producerId
        );
        this.alir1035NoPremium =
          this.alirScheduled &&
          (await ldInstance.getFeatureFlagValueAsBooleanWithId(FeatureFlags.alir1035NoPremium, producerId));
        this.alir1035Premium =
          this.alirScheduled &&
          (await ldInstance.getFeatureFlagValueAsBooleanWithId(FeatureFlags.alir1035Premium, producerId));
        this.is1035ExchangeEnabled = await ldInstance.getFeatureFlagValueAsBooleanWithId(
          FeatureFlags.exchange1035,
          producerId
        );
        this.cirEnabled = await ldInstance.getFeatureFlagValueAsBooleanWithId(FeatureFlags.cirEnabled, producerId);
        this.isNwmReplacementsEnabled = await ldInstance.getFeatureFlagValueAsBooleanWithId(
          FeatureFlags.nwmReplacementsEnabled,
          producerId
        );
        this.lowFaceWholeLifeEnabled = await ldInstance.getFeatureFlagValueAsBooleanWithId(
          FeatureFlags.lowFaceWholeLifeEnabled,
          producerId
        );
        this.editAppEnabled = await ldInstance.getFeatureFlagValueAsBooleanWithId(
          FeatureFlags.editAppEnabled,
          producerId
        );
        this.isBackDatingAmendmentEnabled = await ldInstance.getFeatureFlagValueAsBooleanWithId(
          FeatureFlags.isBackDatingAmendmentEnabled,
          producerId
        );
        this.isDocuSignRedirectEnabled = await ldInstance.getFeatureFlagValueAsBooleanWithId(
          FeatureFlags.isDocuSignRedirectEnabled,
          producerId
        );
        this.inForceIllustrationEnabled = await ldInstance.getFeatureFlagValueAsBooleanWithId(
          FeatureFlags.inForceIllustrationEnabled,
          producerId
        );
        this.lowerDividendRateEnabled = await ldInstance.getFeatureFlagValueAsBooleanWithId(
          FeatureFlags.lowerDividendRateEnabled,
          producerId
        );

        this.isJuvenilePolicyEnabled = await ldInstance.getFeatureFlagValueAsBooleanWithId(
          FeatureFlags.isJuvenilePolicyEnabled,
          producerId
        );
        this.artEnabled = await ldInstance.getFeatureFlagValueAsBooleanWithId(FeatureFlags.artEnabled, producerId);
        this.clientSiteAuditingEnabled = await ldInstance.getFeatureFlagValueAsBooleanWithId(
          Controller.CLIENT_SITE_AUDITING_ENABLED_FLAG,
          producerId
        );

        this.earlyInsuredConsentEnabled = await ldInstance.getFeatureFlagValueAsBooleanWithId(
          FeatureFlags.earlyInsuredConsentEnabled,
          producerId
        );
        this.annuityEnabled = await ldInstance.getFeatureFlagValueAsBooleanWithId(FeatureFlags.annuity, producerId);

        this.isCoverpathNYEnabled = await ldInstance.getFeatureFlagValueAsBooleanWithId(
          FeatureFlags.isCoverpathNYEnabled,
          producerId
        );
        this.nonroundedFaceAmountsEnabled = await ldInstance.getFeatureFlagValueAsBooleanWithId(
          FeatureFlags.nonroundedFaceAmountsEnabled,
          producerId
        );

        //TODO with ticket HL-49932: Remove Instances of isTeleCMIEnabled feature flag
        this.isTeleCMIEnabled = await ldInstance.getFeatureFlagValueAsBooleanWithId(
          FeatureFlags.isTeleCMIEnabled,
          producerId
        );

        this.mmGiactWebDebitVerificationServiceEnabled = await ldInstance.getFeatureFlagValueAsJSON(
          FeatureFlags.mmClientGiactWebDebitVerificationServiceEnabled,
          producerId,
          null
        );

        this.enableConversionAutomation = await ldInstance.getFeatureFlagValueAsBooleanWithId(
          FeatureFlags.enableConversionAutomation,
          producerId
        );

        this.altSubmitSignVerbiageEnabled = await ldInstance.getFeatureFlagValueAsBooleanWithId(
          FeatureFlags.altSubmitSignVerbiageEnabled,
          producerId
        );

        this.isQuestionLockEnabled = await ldInstance.getFeatureFlagValueAsBooleanWithId(
          FeatureFlags.isQuestionLockEnabled,
          producerId
        );

        this.enableAuthPayforDDB = await ldInstance.getFeatureFlagValueAsBooleanWithId(
          FeatureFlags.enableAuthPayforDDB,
          producerId
        );

        this.enableFailedPaymentRetry = await ldInstance.getFeatureFlagValueAsBooleanWithId(
          FeatureFlags.enableFailedPaymentRetry,
          producerId
        );

        this.lowFaceWL10EnableDate = await ldInstance.getFeatureFlagValueAsString(
          FeatureFlags.lowFaceWL10EnableDate,
          producerId
        );

        // configure the power form id in launch darkly feature flag, for better configurability
        this.docusignClientCancelPowerFormId = await ldInstance.getFeatureFlagValueAsString(
          FeatureFlags.docusignClientCancelPowerFormId,
          producerId
        );

        this.cpClientCancelTermPolicyPowerFormId = await ldInstance.getFeatureFlagValueAsString(
          FeatureFlags.cpClientCancelTermPolicyPowerFormId,
          producerId
        );

        this.cpClientChangeFaceAmountPowerFormId = await ldInstance.getFeatureFlagValueAsString(
          FeatureFlags.cpClientChangeFaceAmountPowerFormId,
          producerId
        );

        this.cpClientChangeNamePowerFormId = await ldInstance.getFeatureFlagValueAsString(
          FeatureFlags.cpClientChangeNamePowerFormId,
          producerId
        );

        this.cpClientChangeContactInfoPowerFormId = await ldInstance.getFeatureFlagValueAsString(
          FeatureFlags.cpClientChangeContactInfoPowerFormId,
          producerId
        );
      }

      this.enableGenesysCobrowse = await ldInstance.getFeatureFlagValueAsBoolean(
        FeatureFlags.enableGenesysCobrowse,
        'cp-client'
      );

      this.nbDecommissionPost30Days = await ldInstance.getFeatureFlagValueAsBoolean(
        FeatureFlags.nbDecommissionPost30Days,
        'cp-client'
      );

      if (this.nbDecommissionPost30Days) {
        this.editAppEnabled = false;
      }

      const diffCycleTime = process.hrtime(startTime);
      const totalMS = diffCycleTime[0] * 1000 + diffCycleTime[1] / 1000000;
      UILoggerSingleton.getInstance().info(
        {
          module: 'Controller.preServerCall',
          function: 'getFeatureFlag',
          category: UICategory.milestone,
        },
        `Time taken ${totalMS} ms`
      );
    }
  }

  createClientQuote(): CoverpathTermQuote {
    const quote = CoverpathTermQuote.new(this.customer);
    quote.isAdvisorQuote = false;
    return quote;
  }

  isCoverpathTermQuote(quote: BaseQuote): boolean {
    return quote instanceof CoverpathTermQuote;
  }

  //TODO - Get working for multiple applications - https://massmutual.atlassian.net/browse/HL-19070
  @remote({ on: 'server' })
  public getContactInfo(useSecondary?: boolean) {
    let policy: Policy;
    if (useSecondary) {
      policy = this.customer.application.secondaryPolicy;
    } else {
      policy = this.customer.application.primaryPolicy;
    }
    return PolicyAdminClient.getContactInfo(policy);
  }

  //TODO - Get working for multiple applications - https://massmutual.atlassian.net/browse/HL-19070
  @remote({ on: 'server' })
  public getPaymentInfo(useSecondary?: boolean) {
    let policy: Policy;
    if (useSecondary) {
      policy = this.customer.application.secondaryPolicy;
    } else {
      policy = this.customer.application.primaryPolicy;
    }
    return PolicyAdminClient.getPaymentInfo(policy);
  }

  /**
   * Checks workflow requirements and changes workflow states
   *
   * @remark This function is duplicated here to fulfill CoverpathControllerInterface contract
   * TODO move this function to a common place as part of HL-14757
   */
  @remote({ on: 'server' })
  checkRequirement(policy: Policy, requirement: string, futureSubState: string, futureState?: string) {
    let reqToCheck: string;
    try {
      reqToCheck = _.find(policy.currentStageRequirements, (req) => {
        return req.match(new RegExp(requirement));
      });
      if (!reqToCheck) {
        LoggerSingleton.getInstance().info(
          {
            module: 'Controller',
            function: 'checkRequirement',
            category: Category.request,
            context: {
              piiLevel: PiiLevelEnum.nonPII,
            },
          },
          `Requirement: '${requirement}' not in currentStageRequirements for policyId:'${policy._id}'`
        );
        return;
      }
    } catch (e) {
      let message: string;
      // Logging out a specific issue, detailed in production logs in tickets: HL-8317/HL-9445
      if (!policy) {
        message = `Error Policy is missing while attempting to satisfy requirement: '${requirement}'`;
      } else {
        message = `Error while attempting to satisfy requirement: '${requirement}' policyId:'${policy._id}'`;
      }
      LoggerSingleton.getInstance().error(
        {
          module: 'Controller',
          function: 'checkRequirement',
          category: Category.request,
          context: {
            piiLevel: PiiLevelEnum.nonPII,
          },
          error: e,
        },
        message
      );
      return;
    }

    let force = false;
    switch (reqToCheck) {
      case 'Accept':
        LoggerSingleton.getInstance().warn(
          {
            module: 'Controller',
            function: 'checkRequirement',
            category: Category.request,
            context: {
              piiLevel: PiiLevelEnum.nonPII,
            },
          },
          "Cannot 'Accept' Policy"
        );
        return;

      case 'Resend Application to Docusign':
        force = true;
        break;
    }
    return policy.fulfillReq(reqToCheck, futureSubState, futureState, force);
  }

  /**
   * NOTES:
   * - runs only if user is logged in
   * - makes a database call
   * @param applicationNumber
   */
  @remote({ on: 'server' })
  async checkApplicationAccess(application: CasApplication | MMDirectApplication): Promise<boolean> {
    const applicationNumber: string = application.applicationNumber;
    if (!this.isLoggedIn()) {
      LoggerSingleton.getInstance().warn(
        {
          module: 'Controller',
          function: 'checkApplicationAccess',
          category: Category.request,
          context: {
            piiLevel: PiiLevelEnum.nonPII,
            referenceType: 'Customer',
            referenceId: this.customer._id,
          },
          data: {
            applicationNumber,
            customerId: this.customer._id,
          },
        },
        `Customer is trying to run this function without logging in`
      );
      return false;
    }

    const canAccessApplication = await CoverpathCustomerUtils.customerHasApplicationAccess(
      this.customer,
      applicationNumber,
      this.amorphic
    );
    return canAccessApplication;
  }

  @remote({ on: 'server' })
  async prepareForPolicySubmission(): Promise<void> {
    await this.applySociety1851(this.customer);
  }

  // serverOnly
  private async applySociety1851(customer: CoverpathCustomer) {
    const policy = customer.application.primaryPolicy as CoverpathPolicy;
    try {
      policy.application.isSociety1851 = await CoverpathUtilsBackEnd.checkSociety1851Qualification(policy);
      policy.application.setDirty();
    } catch (error) {
      LoggerSingleton.getInstance().warn(
        {
          module: 'Controller',
          function: 'applySociety1851',
          category: Category.request,
          context: {
            piiLevel: PiiLevelEnum.nonPII,
            referenceType: 'Policy',
            referenceId: policy._id,
          },
          error: error,
        },
        `error setting isSociety1851 flag, policyId is ${policy._id}`
      );
    }
  }

  public isApplicationSubmitted(): boolean {
    return !!_.get(this, 'customer.application.primaryPolicy.submittedAt', false);
  }

  protected inNonProductionMode(): boolean {
    return false;
  }

  // Callback from amorphic when the session expires. Just redirect back to the default route
  publicExpireSession() {
    const newLocation = `${window.location.protocol}//${window.location.host}`;
    window.location.href = newLocation;
  }

  protected async postProcessEmailChange(): Promise<void> {
    // NoOp
  }

  protected async returnInformationToClient(
    identifiedPrincipal: IdentifiedPrincipal
  ): Promise<AuthenticationInformation> {
    const principal = (identifiedPrincipal && identifiedPrincipal.principal) || null;
    UILoggerSingleton.getInstance().warn(
      {
        module: 'Controller',
        function: 'returnInformationToClient',
        category: UICategory.request,
        context: {
          referenceType: 'Principal',
          referenceId: (principal && principal._id) || null,
        },
        data: {
          principal: (principal && principal._id) || null,
          isExistingUser: identifiedPrincipal ? identifiedPrincipal.isExistingUser : 'Unavailable',
        },
      },
      'Returning info to client for associated user'
    );

    const authInformation = {
      customerId: this.customer._id,
      isExistingUser: identifiedPrincipal.isExistingUser,
    };

    return authInformation;
  }

  /**
   * Initializes new account info for accounts created on the Client Account Center via Generic Links.
   * The customer object graph is created by the Quick Quote component, and passed here for finalization.
   *
   * New Generic Link object graphs for existing customers is handled in `initializeFromQuickQuote`
   */
  protected async runNewAccountActions(): Promise<void> {
    const loggerData = {
      module: 'Controller',
      activity: 'runNewAccountActions',
      category: UICategory.milestone,
      context: {
        referenceType: 'Customer',
        referenceId: this.customer._id,
      },
      data: {
        auth0Id: this.auth0userId,
        customerId: this.customer._id,
      },
    };

    UILoggerSingleton.getInstance().info(loggerData, 'Running New Account Actions');
    const agent = this.customer.application.primaryPolicy.primaryPolicyAgent.primaryAgent;
    const newCustomerProps = this.makePropsForNewCustomer();
    this.customer.initializeQuote(newCustomerProps.quoteType);
    this.customer.reset();
    this.customer.updateProps(newCustomerProps);

    /**
     * For new accounts created with the generic link, we take the existing customer object graph
     * created by the Quick Quote component, and just finalize it as the user's new account.
     */
    await this.initializeNewGenericLinkApp(this.customer, agent, newCustomerProps.address.state);

    this.cpFinalizeRegistration();
    UILoggerSingleton.getInstance().info(loggerData, 'Finished running new account actions');
  }

  protected async runPostLoginActions(): Promise<void> {
    const email: string = this.getPrincipal().email;
    try {
      const amcUserInfo: AMCAuth0ResponseUser = await this.getAMCController().getAMCUserInfo(email);
      // Debugger for the ticket HL-42869
      if (this.customer.primaryQuote.productType === null) {
        UILoggerSingleton.getInstance().info(
          {
            module: 'Controller',
            function: 'runPostLoginActions',
            category: UICategory.milestone,
            data: {
              productType: this.customer.primaryQuote,
            },
          },
          'HL-42869: ProductType null issue'
        );
      }

      if (amcUserInfo && amcUserInfo.mfa_verified) {
        this.customer.hasRegistered = true;
        await this.customer.persistSave();
      }

      await this.initializeFromQuickQuote();
    } catch (e) {
      const err = {
        name: e?.name,
        message: e?.message,
        stack: e?.stack,
        data: {
          email,
        },
      };
      UILoggerSingleton.getInstance().error(
        {
          module: 'Controller',
          function: 'runPostLoginActions',
          category: UICategory.milestone,
          context: {
            piiLevel: UIPiiLevelEnum.s2,
          },
          error: err,
        },
        'Failed running post login actions'
      );

      throw { code: 'failed_post_login_actions', text: 'Something went wrong.' };
    }
  }

  @remote({ on: 'server' })
  setQuickQuoteForm(quote: QuoteForm) {
    this.quickQuoteForm = quote;
  }

  @remote({ on: 'server' })
  clearQuickQuoteForm() {
    this.quickQuoteForm = null;
  }

  async initializeFromQuickQuote(): Promise<void> {
    if (!this.quickQuoteForm) {
      return;
    }
    const quoteForm = this.quickQuoteForm;
    this.clearQuickQuoteForm();
    quoteForm.clientInformation.ownerFirstName = this.customer.firstName;
    quoteForm.clientInformation.ownerLastName = this.customer.lastName;

    try {
      const agentResultSet = await Agent.persistorFetchByQuery({ producerId: quoteForm.producerId });
      if (agentResultSet.length === 0) {
        throw new Error(`Could not find agent for new policy with ID ${quoteForm.producerId}`);
      }

      const customer = await ObjectGraphController.buildCustomerObjectGraph(
        this.customer,
        quoteForm,
        this.isTeleCMIEnabled,
        !!this.lowFaceWholeLifeEnabled
      );

      const state = quoteForm.clientInformation.contractState;
      await this.initializeNewGenericLinkApp(customer, agentResultSet[0], state);
    } catch (error) {
      const err = {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };
      UILoggerSingleton.getInstance().error(
        {
          module: 'controller',
          activity: 'initializeFromQuickQuote',
          category: UICategory.request,
          error: {
            ...err,
            data: {
              producerId: quoteForm.producerId,
            },
          },
        },
        'Error creating quick quote app'
      );
    }
  }

  private async initializeNewGenericLinkApp(customer: CoverpathCustomer, agent: Agent, state: string): Promise<void> {
    this.clearQuickQuoteForm(); // QQ Form not used for first apps, so always delete to be safe
    customer.application.primaryPolicy.ownerPersonResidentialAddress.state = state;
    customer.application.isAgentAssistant = false;
    customer.application.setSource(ApplicationSourceType.GENERIC_LINK);

    await customer.application.addPolicyFollowers(agent, Entitlement.AGENT);
    const defaultAgencyProps: AgencyCorpsRelationship = {
      agencyBpid: agent.agencyId,
      corpsBpid: agent.corpId,
      agencyName: agent.agencyName,
      corpsName: null,
    };
    const policyAgent = agent.asPolicyAgent(customer.application.primaryPolicy);
    customer.application.primaryPolicy.primaryPolicyAgent = policyAgent;
    policyAgent.agencyId = agent.agencyId;
    policyAgent.corpId = agent.corpId;
    policyAgent.corpAgency = JSON.stringify(defaultAgencyProps);

    await this.commissionSplitsController.createCommissionSplits(customer.application.primaryPolicy);
    await this.applicationLockController.setApplicationLockedBy(LockedByUserType.CUSTOMER);
    this.customer.registerPostServerCallCallback(() => this.customer.application.startFailureNotificationWorkflow());

    try {
      UILoggerSingleton.getInstance().info(
        {
          module: 'Controller',
          activity: 'initializeNewGenericLinkApp',
          category: UICategory.request,
          data: {
            auth0Id: this.auth0userId,
            customerId: this.customer._id,
          },
        },
        'Saving the new customer object graph'
      );
      await this.customer.cascadeSave(undefined);
    } catch (cascadeSaveError) {
      const err = {
        name: cascadeSaveError.name,
        message: cascadeSaveError.message,
        stack: cascadeSaveError.stack,
      };
      UILoggerSingleton.getInstance().error(
        {
          module: 'coverpathClient Controller',
          function: 'initializeNewGenericLinkApp',
          category: UICategory.request,
          error: {
            ...err,
            data: {
              customer: {
                id: this.customer._id,
                email: this.customer.email,
              },
              agent: {
                id: policyAgent.agent ? policyAgent.agent._id : null,
                producer: policyAgent.agent ? policyAgent.agent.producerId : null,
              },
            },
          },
        },
        'There was an issue initializing the generic link app'
      );

      throw cascadeSaveError;
    }
  }

  protected async updateAuthOwner(authOwner: AuthOwner): Promise<void> {
    // NoOp
  }

  protected async updateIdentity(principal: PrincipalCustomer): Promise<void> {
    const identityService = this.getIdentityService();
    try {
      let identity: Identity = await identityService.loadIdentityByIdentifier(this.auth0userId);

      if (this.isAuth0Enabled) {
        if (!identity) {
          identity = await identityService.createNewIdentity(this.auth0userId, true, undefined);
          await identityService.associateApplicationWithIdentity(this.customer, identity);
        } else {
          const authorizedAccount: AccountAuthorization = identity.account.accountAuthorizations.find((accountAuth) => {
            return accountAuth.managedEntityType.templateName === CoverpathCustomer.amorphicClassName;
          });
          if (!authorizedAccount) {
            await identityService.associateApplicationWithIdentity(this.customer, identity);
          }
        }
        this.authenticatedIdentity = identity;
      }
    } catch (e) {
      const err = {
        name: e?.name,
        message: e?.message,
        stack: e?.stack,
        data: {
          email: principal ? principal.email : null,
        },
      };
      UILoggerSingleton.getInstance().error(
        {
          module: 'Controller',
          function: 'updateIdentity',
          category: UICategory.request,
          context: {
            piiLevel: UIPiiLevelEnum.s2,
            referenceType: 'Principal',
            referenceId: principal ? principal._id : null,
          },
          error: err,
        },
        'failed updating identity'
      );

      throw { code: 'failed_updating_identity', text: 'Something went wrong.' };
    }
  }

  @remote({ on: 'server' })
  public getBeneficiaryInfo(useSecondary?: boolean) {
    let policy: Policy;
    if (useSecondary) {
      policy = this.customer.application.secondaryPolicy;
    } else {
      policy = this.customer.application.primaryPolicy;
    }
    return PolicyAdminClient.getBeneficiaryInfo(policy);
  }

  protected async fetchData(): Promise<boolean> {
    await this.fetchAccessories();

    return true;
  }

  @remote({ on: 'server' })
  public async resolveAuth0Data(): Promise<void> {
    await this.setAuth0Configuration();

    if (this.isAuth0Enabled) {
      await this.getAMCController().getAMCUserInfo();
    }

    await this.resolveAccountVerificationStatus();
  }

  public async lookUpIsAuth0Enabled(): Promise<boolean> {
    return Auth0Utils.isAuth0EnabledByProvider(AppConfigTypes.AUTH0_ENABLED_COVERPATH);
  }

  protected async postAuthExecution(principal: PrincipalCustomer, isExistingAccount: boolean): Promise<void> {
    const loggerData = {
      module: 'Controller',
      function: 'postAuthExecution',
      category: UICategory.request,
      context: {
        piiLevel: UIPiiLevelEnum.s2,
        referenceType: 'Principal',
        referenceId: principal ? principal._id : null,
      },
      data: {
        email: this.email,
        principal: principal ? principal._id : null,
        role: principal ? principal.role : null,
      },
    };

    UILoggerSingleton.getInstance().info(loggerData, 'Beginning post auth execution steps');

    await super.postAuthExecution(principal, isExistingAccount);

    if (!Utils.equalsIgnoreCase(this.customer.email, this.email)) {
      this.customer.email = this.email;
      UILoggerSingleton.getInstance().info(
        loggerData,
        'Updated customer email to reflect different email used during registration'
      );
    }

    if (!this.customer.application.primaryPolicy.submittedAt) {
      this.enforceEmailConstraints(this.customer.application.primaryPolicy);
    }

    // Marketing Segment
    this.identifyToSegment(this.customer._id, {
      user_type: 'customer',
      user_id: this.customer._id,
      password_set_date: new Date().toString(),
      link_sent_date: this.customer.application.primaryPolicy.linkSentDate,
      producer_id: this.customer.application.primaryPolicy.primaryPolicyAgent.agent.producerId,
      link_type: this.customer.application.isAgentAssistant
        ? 'agent_assisted'
        : this.customer.application.quote.isAdvisorQuote
        ? 'personalized'
        : 'generic',
    });

    UILoggerSingleton.getInstance().info(loggerData, 'Completed post auth execution steps');
  }

  private enforceEmailConstraints(policy: CoverpathPolicy) {
    if (!policy.owner.email) {
      policy.owner.email = this.customer.email;
    }
    if (!!policy.ownerEntity) {
      return; // TODO - Business/Trust ownership constraints HL-53344
    }
    if (
      policy.ownerAndInsuredEmailsShouldBeSame() &&
      !Utils.equalsIgnoreCase(policy.insured.person.email, policy.owner.email)
    ) {
      policy.insured.person.email = policy.owner.email;
    } else if (
      !policy.ownerAndInsuredEmailsShouldBeSame() &&
      Utils.equalsIgnoreCase(policy.insured.person.email, policy.owner.email)
    ) {
      policy.insured.person.email = null;
    }
    if (
      policy.insuredIsJuvenileHasSigner() &&
      Utils.equalsIgnoreCase(policy.insured.signer.email, policy.owner.email)
    ) {
      policy.insured.signer.email = null;
    }
  }

  @remote({ on: 'server' })
  public async checkIfAccountVerified(): Promise<boolean> {
    let isVerified = false;

    if (this.isAccountVerified) {
      isVerified = true;
    } else {
      if (this.isAuth0Enabled) {
        const accountInfo: AMCAuth0ResponseUser = await this.getAMCController().getAMCUserInfo(this.email);
        if (accountInfo) {
          isVerified = accountInfo.email_verified;
        }
      }
    }

    return isVerified;
  }

  public getPath(): SiteType {
    return SiteType.CLIENT_SITE;
  }

  protected async preAuthExecution(): Promise<string> {
    // this has to occur first so a sanderling customer does not see the
    // 'we couldn't find your financial professional' screen
    // the customer's email may not exist on the customer object,and the customer object itself may not be
    // loaded yet
    await this.sanderlingPolicyRepairController.repairSanderlingPolicies();

    this.inviteController.validateSessionEmailAgainstQuote();

    const hasServicingAgent: boolean = await CoverpathCustomerUtils.customerHasServicingAgent(
      this.email,
      this.customer
    );

    if (!hasServicingAgent) {
      UILoggerSingleton.getInstance().error(
        {
          module: 'Controller',
          function: 'preAuthExecution',
          category: UICategory.request,
          context: {
            referenceType: 'Customer',
            referenceId: this.customer._id,
          },
          error: {
            isHumanRelated: false,
            data: {
              ids: Utils.extractIds(this.customer),
            },
          },
        },
        'Customer is missing a servicing agent'
      );
      throw { code: 'no_associated_advisor', text: 'Customer does not exist and has no advisor' };
    }

    const onlyDraftInProcess: boolean = await CoverpathCustomerUtils.customerHasOnlyDraftInProcess(this.email);
    if (onlyDraftInProcess && !this.quickQuoteForm) {
      UILoggerSingleton.getInstance().warn(
        {
          module: 'Controller',
          function: 'preAuthExecution',
          category: UICategory.request,
          context: {
            referenceType: 'Customer',
            referenceId: this.customer._id,
          },
          data: {
            ids: Utils.extractIds(this.customer),
          },
        },
        'Customer only has draft(s) in process'
      );
      throw { code: 'draft_in_progress', text: 'Customer only has draft(s) in process' };
    }

    await this.verifyCustomerIdentityMatchesAuth0Id(this.getPrincipal());

    UILoggerSingleton.getInstance().info(
      {
        module: 'Controller',
        function: 'preAuthExecution',
        category: UICategory.request,
        context: {
          piiLevel: UIPiiLevelEnum.s2,
        },
        data: {
          hasServicingAgent: hasServicingAgent,
          onlyDraftInProcess: onlyDraftInProcess,
          controllerEmail: this.email,
        },
      },
      'Running preAuthExecution steps'
    );

    return '';
  }

  protected async postGetPrincipal(): Promise<void> {
    UILoggerSingleton.getInstance().info(
      {
        module: 'Controller',
        function: 'postGetPrincipal',
        category: UICategory.request,
        context: {
          piiLevel: UIPiiLevelEnum.s2,
        },
        data: {
          email: this.email,
        },
      },
      'Auth0 postGetPrincipal'
    );
  }

  public getAuth0Configurations(): Auth0Configs {
    return Auth0Utils.getAuth0Configuration(Auth0Channel.COVERPATH);
  }

  public getAuth0Secret(): string {
    return Auth0Utils.getAuth0Secret(Auth0Channel.COVERPATH);
  }

  public async exchangeAuth0CodeForToken(code: string, redirectUri: string): Promise<Auth0Response> {
    return Auth0Client.exchangeCodeForToken(code, redirectUri, Auth0Channel.COVERPATH);
  }

  protected setAuth0Properties(response: Auth0Response) {
    super.setAuth0Properties(response);
    if (response.access_token) {
      const decodedToken: jwt.JwtPayload = this.decodeJwtToken(response.access_token);
      const cvpIdKey: string = `${Auth0Utils.getAMCTokenNamespacePrefix()}cvp_id`;
      const amcGuidClaim = this.amorphic.config.nconf.get('amcGuidClaim');
      this.amcGuid = decodedToken && decodedToken[amcGuidClaim];
      const cvpId: string = decodedToken[cvpIdKey];
      this.managedEntityId = cvpId || '';

      UILoggerSingleton.getInstance().info(
        {
          module: 'Controller',
          function: 'setAuth0Properties',
          category: UICategory.milestone,
          data: {
            cvpIdKey: cvpIdKey,
            managedEntityId: this.managedEntityId || '',
          },
        },
        'Set managed entity ID from access token'
      );
    }
  }

  public getAuth0LoginHint(): string {
    return this.customer.application && this.customer.application.isAgentAssistant
      ? Auth0Utils.getCustomerId(this.customer)
      : null;
  }

  @remote({ on: 'server' })
  public async resolveHeavyUserFeature(): Promise<boolean> {
    this.isHeavyUserEnabled = await Auth0Utils.isAuth0EnabledByProvider(AppConfigTypes.HEAVY_USER_ENABLED);
    return this.isHeavyUserEnabled;
  }

  // fetch heavy user data from amc and set it for the client app
  @remote({ on: 'server' })
  public async getHeavyUserAMCdata(): Promise<void> {
    if (this.isHeavyUserEnabled) {
      LoggerSingleton.getInstance().info(
        {
          module: 'AMCController',
          function: 'getHeavyUserAMCdata',
          category: Category.request,
          context: {
            piiLevel: PiiLevelEnum.nonPII,
          },
        },
        'Going to get amc heavy user data'
      );

      await this.getAMCController().getHeavyUserAMCdata();
      this.customerFirstLastName = await this.getAMCController().getHeavyUserNameOnly();

      if (this.getAMCController().AMCHeavyUserData) {
        await this.setAMCuserInfoForCustomer();
      }
    }
  }

  @remote({ on: 'server' })
  private async handleChangesForHeavyUserProps(): Promise<void> {
    const insuredSSNwasAlreadySet =
      CoverpathCustomerUtils.formatSSN(this.customer.application.primaryPolicy.insured.person.SSN) ===
      CoverpathCustomerUtils.formatSSN(this.getAMCController().AMCHeavyUserData.governmentId);
    const ownerSSNwasAlreadySet =
      CoverpathCustomerUtils.formatSSN(this.customer.application.primaryPolicy.ownerPerson.SSN) ===
      CoverpathCustomerUtils.formatSSN(this.getAMCController().AMCHeavyUserData.governmentId);

    const setInsuredHeavyUserInfo =
      this.customer.application.primaryPolicy.isInsuredInfoSameAsOwners() && !insuredSSNwasAlreadySet;
    const clearInsuredHeavyUserInfo =
      !this.customer.application.primaryPolicy.isInsuredInfoSameAsOwners() && insuredSSNwasAlreadySet;
    if (setInsuredHeavyUserInfo) {
      await this.setInsuredHeavyUserInfo();
    } else if (clearInsuredHeavyUserInfo) {
      // clear insured pii
      await this.clearInsuredHeavyUserInfo();
    } else if (!ownerSSNwasAlreadySet) {
      await this.setOwnerHeavyUserInfo();
    } else if (!this.customer.application.primaryPolicy.ownerPerson.email) {
      await this.resetOwnerEmail();
    }
  }

  private async setInsuredHeavyUserInfo(): Promise<void> {
    await this.setPersonsDataToHeavyUserData(this.customer.application.primaryPolicy.insured.person, true);

    // necessary? heartbeat?
    this.customer.persistSave();
  }

  private async setOwnerHeavyUserInfo(): Promise<void> {
    await this.setPersonsDataToHeavyUserData(this.customer.application.primaryPolicy.ownerPerson, true);

    // necessary? heartbeat?
    this.customer.persistSave();
  }

  private async clearInsuredHeavyUserInfo(): Promise<void> {
    this.clearPersonsHeavyUserData(this.customer.application.primaryPolicy.insured.person);

    // necessary?
    this.customer.persistSave();
  }

  private async resetOwnerEmail(): Promise<void> {
    this.customer.application.primaryPolicy.ownerPerson.email = this.customer.email;

    this.customer.persistSave();
  }

  private async setAMCuserInfoForCustomer(): Promise<void> {
    UILoggerSingleton.getInstance().info(
      {
        module: 'Controller',
        function: 'setAMCuserInfoForCustomer',
        category: UICategory.request,
      },
      'yo we want to set the amc pii to the customer object now'
    );

    await this.setPersonsDataToHeavyUserData(this.customer.application.primaryPolicy.ownerPerson, true);

    // in case name is not set then update it for the owner
    if (!this.customer.firstName) {
      await this.setPersonsDataToHeavyUserData(this.customer);
    }
    if (this.customer.application.primaryPolicy.isInsuredInfoSameAsOwners()) {
      await this.setPersonsDataToHeavyUserData(this.customer.application.primaryPolicy.insured.person, true);
    }

    UILoggerSingleton.getInstance().info(
      {
        module: 'Controller',
        function: 'setAMCuserInfoForCustomer',
        category: UICategory.request,
      },
      'yo we set the info and it should show up as readonly for client'
    );

    // necessary?
    await this.customer.persistSave();
  }

  private async setPersonsDataToHeavyUserData(person: Person | Customer, setAllData?: boolean): Promise<void> {
    person.firstName = this.getAMCController().AMCHeavyUserData.firstName;
    person.lastName = this.getAMCController().AMCHeavyUserData.lastName;
    if (person instanceof Person) {
      person.middleName = this.getAMCController().AMCHeavyUserData.middleName;
      // dont need to set the PII every time
      if (setAllData) {
        person.dob = new Date(this.getAMCController().AMCHeavyUserData.birthDate);
        person.SSN = CoverpathCustomerUtils.formatSSN(this.getAMCController().AMCHeavyUserData.governmentId);
        // Below line is for completeness and is not triggered because AMC heavy user flow is off
        await this.setPiiByPersonId(person._id, 'SSN', person.SSN);
      }
    }
  }

  private clearPersonsHeavyUserData(person: Person): void {
    person.dob = null;
    person.SSN = null;
  }

  private identifyToSegment(userId: string, traits: any): void {
    const analytics = new Analytics(this.segmentKey);
    try {
      analytics.identify({
        userId: userId,
        traits: traits,
      });
    } catch (e) {
      // Log and ignore any segment errors as we don't want these errors interrupting the
      // login process
      UILoggerSingleton.getInstance().warn(
        {
          module: 'controller',
          function: 'identifyToSegment',
          category: UICategory.request,
          error: e,
        },
        'Segment Error'
      );
    }
  }

  @remote({ on: 'server' })
  async getStateLicenses(producerId: string): Promise<AgentLicense[] | undefined> {
    let licenses: AgentLicense[];
    try {
      licenses = await new CdmService().getStateLicenses(producerId);
    } catch (e) {
      const err = {
        name: e?.name,
        message: e?.message,
        stack: e?.stack,
        data: {
          producerId: producerId,
        },
      };
      LoggerSingleton.getInstance().error(
        {
          module: 'controller',
          function: 'getStateLicenses',
          category: Category.request,
          context: {
            piiLevel: PiiLevelEnum.nonPII,
          },
          error: err,
        },
        'Error fetching agent licenses'
      );
    }
    return licenses;
  }

  @remote({ on: 'server' })
  public async insuredAcknowledgementForAutomation(): Promise<any> {
    const ret = { loaded: true, data: false };
    if (!Utils.isProdEnv()) {
      const policy = this.customer.application.primaryPolicy;
      const cancelled = policy ? policy.wasCanceledDeclined() : true;
      if (cancelled) {
        ret.data = false;
      } else {
        await policy.fulfillRequirements(['IC1R4 Insured consent has been received']);
        ret.data = true;
        const signer = (policy as CoverpathPolicy).insured.getInsuredSigner();
        await ESignSessionMetadata.getKnex().where('_id', '=', signer.esignMetadata._id).update({
          validationAttempts: null,
          linkGeneratedAt: null,
        });
      }
    }
    return ret;
  }

  @remote({ on: 'server' })
  public async insuredAcknowledgement(token: string): Promise<any> {
    const secret: string = this.amorphic.config.nconf.get('insuredAcknowledgeKey');
    let data;
    const ret = { loaded: true, data: false };
    try {
      data = jwt.verify(token, secret);
    } catch (err) {
      ret.data = false;
    }
    if (data) {
      const policies = await this.getPolicy(data.polno);
      const policy = policies[0];
      const cancelled = policy ? policy.wasCanceledDeclined() : true;
      if (cancelled) {
        ret.data = false;
      } else {
        if (data.recipient === 'owner') {
          await policy.fulfillRequirements(['IC1R7 Owner consent has been received']);
          const signer = (policy as CoverpathPolicy).getOwnerSigningPerson();
          await ESignSessionMetadata.getKnex().where('_id', '=', signer.esignMetadata._id).update({
            validationAttempts: null,
            linkGeneratedAt: null,
          });
        } else {
          await policy.fulfillRequirements(['IC1R4 Insured consent has been received']);
          const signer = (policy as CoverpathPolicy).insured.getInsuredSigner();
          await ESignSessionMetadata.getKnex().where('_id', '=', signer.esignMetadata._id).update({
            validationAttempts: null,
            linkGeneratedAt: null,
          });
        }

        const signerParty =
          data.recipient === 'owner' ? policy.getOwnerSigningPerson() : policy.insured.getInsuredSigner();
        await Policy.getKnex()
          .insert({
            _id: uuid.v4().toString(),
            _template: 'PolicyDocAction',
            __version__: 1,
            party_id: signerParty._id,
            policy_doc_id: null,
            policy_doc_name: 'Application',
            comment: `${data.recipient} Acknowledged`,
            action: POLICY_DOC_ACTION_TYPE.ACK,
            ipAddress: this.amorphic['incomingIP'],
          })
          .into('policy_doc_action');
        ret.data = true;
      }
    }
    return ret;
  }

  @remote({ on: 'server' })
  async getPolicy(policyNumber: string) {
    return Policy.getFromPersistWithQuery({ policyNumber });
  }

  private getAnnuityCosigner(recipient: AnnuityAdditionalSigners, annuity: CoverpathAnnuity): Person {
    let signer: Person;
    if (recipient === 'jointowner') {
      signer = annuity.otherOwner;
    } else if (recipient === 'jointannuitant') {
      signer = annuity.jointAnnuitant;
    } else if (recipient === 'primaryannuitant') {
      signer = annuity.annuitant;
    }

    return signer;
  }

  @remote({ on: 'server' })
  async setupAnnuityDocumentsForAdditionalEsign(jwtToken: string) {
    const { polno, recipient } = await this.parseAnnuitytoken(jwtToken);
    const policies = await Policy.persistorFetchByQuery({ policyNumber: polno });
    const annuity = policies[0] as CoverpathAnnuity;
    const state = annuity.getApplicationState();
    const contractId = annuity._id.toString();
    const docQuery = { $and: [{ policyId: contractId }, { isActive: 'true' }] };
    const allPolicyDocs: PolicyDoc[] = await PolicyDoc.persistorFetchByQuery(docQuery, {
      session: this.amorphic,
      transient: true,
    });

    LoggerSingleton.getInstance().info(
      {
        module: 'controller',
        function: 'setupAnnuityDocumentsForAdditionalEsign',
        category: Category.request,
        context: {
          piiLevel: PiiLevelEnum.s2,
        },
        data: {
          polno,
          allPolicyDocs: allPolicyDocs.length,
        },
      },
      'Total Docs count'
    );

    const formsToSign = await AnnuityDocuments.fetchDocsForAdditionalEsign(
      allPolicyDocs,
      annuity.product,
      state,
      recipient,
      this.getPath(),
      jwtToken
    );
    return formsToSign;
  }

  @remote({ on: 'server' })
  async onAnnuityAdditionalEsignComplete(jwtToken: string) {
    const { polno, recipient } = await this.parseAnnuitytoken(jwtToken);
    let req = '';
    if (recipient === 'jointowner') {
      req = 'AS1R1 Joint owner signed';
    } else if (recipient === 'primaryannuitant') {
      req = 'AS1R5 Primary annuitant signed';
    } else if (recipient === 'jointannuitant') {
      req = 'AS1R3 Joint annuitant signed';
    }

    if (req) {
      const policies = await Policy.persistorFetchByQuery({ policyNumber: polno });
      const policy = policies[0];
      LoggerSingleton.getInstance().info(
        {
          module: 'controller',
          function: 'onAnnuityAdditionalEsignComplete',
          category: Category.request,
          context: {
            piiLevel: PiiLevelEnum.s2,
          },
          data: {
            polno,
            req,
          },
        },
        'Fulfilling requirement'
      );
      await policy.fulfillRequirements([req]);
    }
  }

  private createAnnuityToken(payload: object, options = {}) {
    const secretKey = this.amorphic.config.nconf.get('annuityPreAuthKey');
    return jwt.sign(payload, secretKey, options);
  }

  private parseAnnuitytoken(jwtToken: string, ssnLastFour = ''): jwt.JwtPayload {
    if (ssnLastFour) {
      const secret: string = this.amorphic.config.nconf.get('annuityPreAuthKey') + ssnLastFour;
      return jwt.verify(jwtToken, secret) as jwt.JwtPayload;
    } else {
      // ssn should've been verified
      const secret: string = this.amorphic.config.nconf.get('annuityPreAuthKey');
      const result: jwt.JwtPayload = jwt.verify(jwtToken, secret) as jwt.JwtPayload;
      if (result.ssnVerified) {
        return result;
      }
    }
  }

  @remote({ on: 'server' })
  async authenticateSigner(jwtToken: string, ssnLastFour: string) {
    const MAX_ATTEMPTS = 6;
    const { polno, recipient } = jwt.decode(jwtToken) as jwt.JwtPayload;
    const contracts = await Policy.persistorFetchByQuery({ policyNumber: polno });
    const contract = contracts[0];
    const signer = this.getAnnuityCosigner(recipient, contract);

    if (!signer.esignMetadata) {
      LoggerSingleton.getInstance().info(
        {
          module: 'controller',
          function: 'authenticateSigner',
          category: Category.request,
          context: {
            piiLevel: PiiLevelEnum.nonPII,
            referenceType: 'Person',
            referenceId: signer._id,
          },
          data: {
            signer: signer._id,
            recipient,
          },
        },
        'esignMetadata not found'
      );
      signer.esignMetadata = ESignSessionMetadata.new();
      signer.esignMetadata.validationAttempts = 0;
    } else {
      LoggerSingleton.getInstance().info(
        {
          module: 'controller',
          function: 'authenticateSigner',
          category: Category.request,
          context: {
            piiLevel: PiiLevelEnum.nonPII,
            referenceType: 'Person',
            referenceId: signer._id,
          },
          data: {
            signer: signer._id,
            esignMetadata: signer.esignMetadata._id,
            recipient,
          },
        },
        'esignMetadata found'
      );
    }
    const { esignMetadata } = signer;
    if (esignMetadata.validationAttempts >= MAX_ATTEMPTS) {
      LoggerSingleton.getInstance().info(
        {
          module: 'controller',
          function: 'authenticateSigner',
          category: Category.request,
          context: {
            piiLevel: PiiLevelEnum.s1,
          },
          data: {
            jwtToken,
          },
        },
        'Maximum attempts exhausted'
      );
      return {
        valid: false,
        attemptsRemaining: 0,
      };
    }
    try {
      const payload = {
        ...(this.parseAnnuitytoken(jwtToken, ssnLastFour) as jwt.JwtPayload),
        // new token will expire in 1 hr to limit misuse
        exp: Math.floor(Date.now() / 1000) + 60 * 60,
        ssnVerified: true,
      };
      LoggerSingleton.getInstance().info(
        {
          module: 'controller',
          function: 'authenticateSigner',
          category: Category.request,
          context: {
            piiLevel: PiiLevelEnum.s1,
          },
          data: {
            jwtToken,
          },
        },
        'annuity additional esign token successfully validated'
      );
      return {
        valid: true,
        attemptsRemaining: MAX_ATTEMPTS - esignMetadata.validationAttempts,
        signerValidatedToken: this.createAnnuityToken(payload),
      };
    } catch (err) {
      LoggerSingleton.getInstance().info(
        {
          module: 'controller',
          function: 'authenticateSigner',
          category: Category.request,
          context: {
            piiLevel: PiiLevelEnum.s1,
          },
          data: {
            jwtToken,
          },
        },
        'Invalid token'
      );

      esignMetadata.validationAttempts += 1;

      return {
        valid: false,
        attemptsRemaining: MAX_ATTEMPTS - esignMetadata.validationAttempts,
      };
    } finally {
      signer.esignMetadata.persistorSave();
      signer.persistorSave();
    }
  }

  @remote({ on: 'server' })
  async validateViewDocumentsUrl(token: string) {
    const MAX_ATTEMPTS = 3;
    const secret: string = this.amorphic.config.nconf.get('insuredAcknowledgeKey');
    let data: ValidateDocumentAccess = {
      polno: '',
      isValid: false,
      signerFirstName: '',
      attemptsRemaining: 0,
      esignCompleted: false,
      urlExpired: false,
      recipient: '',
    };
    try {
      data = jwt.verify(token, secret) as ValidateDocumentAccess;
      data.isValid = true;
      LoggerSingleton.getInstance().info(
        {
          module: 'controller',
          function: 'validateViewDocumentsUrl',
          category: Category.request,
          context: {
            piiLevel: PiiLevelEnum.s1,
          },
          data: {
            token,
            data,
          },
        },
        'View Documents token successfully validated'
      );
    } catch (err) {
      LoggerSingleton.getInstance().info(
        {
          module: 'controller',
          function: 'validateViewDocumentsUrl',
          category: Category.request,
          context: {
            piiLevel: PiiLevelEnum.s1,
          },
          data: {
            token,
          },
        },
        'Invalid token'
      );
      data.isValid = false;
    }

    if (data.isValid) {
      const policies = await this.getPolicy(data.polno);
      const policy = policies[0];
      data.urlExpired = CoverpathUtils.isPolicyCanceled(policy);
      const signer = data.recipient === 'owner' ? policy.getOwnerSigningPerson() : policy.insured.getInsuredSigner();
      data.signerFirstName = signer.firstName;
      data.attemptsRemaining = MAX_ATTEMPTS - signer.esignMetadata.validationAttempts;
    }

    LoggerSingleton.getInstance().info(
      {
        module: 'controller',
        function: 'validateViewDocumentsUrl',
        category: Category.request,
        context: {
          piiLevel: PiiLevelEnum.s1,
        },
        data: {
          token,
          ValidateDocumentAccess: data,
        },
      },
      'Insured/Owner View Documents data'
    );
    return data;
  }

  @remote({ on: 'server' })
  async validateSSNAndGetDocsToView(policyNumber: string, ssnLastFour: string, recipient: string) {
    const MAX_ATTEMPTS = 3;
    const data = {
      documents: [],
      attemptsRemaining: 0,
    };
    const policies = await this.getPolicy(policyNumber);
    const policy: CoverpathPolicy = policies[0];
    const signer = recipient === 'owner' ? policy.getOwnerSigningPerson() : policy.insured.getInsuredSigner();
    let validationAttempts = signer.esignMetadata.validationAttempts || 0;
    data.attemptsRemaining = MAX_ATTEMPTS - validationAttempts;
    if (validationAttempts > 2) {
      LoggerSingleton.getInstance().info(
        {
          module: 'controller',
          function: 'validateSSNAndGetDocsToView',
          category: Category.request,
          context: {
            piiLevel: PiiLevelEnum.nonPII,
            referenceType: 'Person',
            referenceId: signer._id,
          },
          data: {
            validationAttempts: data,
            policyNumber,
            partyId: signer._id,
          },
        },
        'Validation attempts exhausted'
      );

      return data;
    }

    const signerLast4 = await SecurityUtils.getPiiValueByKey(signer.SSN, 4);
    if (signerLast4 === ssnLastFour) {
      data.documents = await this.getDocNamesToBeViewed(policy);
      LoggerSingleton.getInstance().info(
        {
          module: 'controller',
          function: 'validateSSNAndGetDocsToView',
          category: Category.request,
          context: {
            piiLevel: PiiLevelEnum.nonPII,
            referenceType: 'Person',
            referenceId: signer._id,
          },
          data: {
            validationAttempts: data,
            policyNumber,
            partyId: signer._id,
          },
        },
        'Successfully validated SSN'
      );

      return data;
    } else {
      validationAttempts++;
      await ESignSessionMetadata.getKnex().where('_id', '=', signer.esignMetadata._id).update({
        validationAttempts,
      });

      data.attemptsRemaining = MAX_ATTEMPTS - validationAttempts;
      LoggerSingleton.getInstance().info(
        {
          module: 'controller',
          function: 'validateSSNAndGetDocsToView',
          category: Category.request,
          context: {
            piiLevel: PiiLevelEnum.nonPII,
            referenceType: 'Person',
            referenceId: signer._id,
          },
          data: {
            validationAttempts: data,
            policyNumber,
            partyId: signer._id,
          },
        },
        'Invalid SSN'
      );

      return data;
    }
  }

  @remote({ on: 'server' })
  async validateInsuredEsignUrl(token: string) {
    const MAX_ATTEMPTS = 3;
    const secret: string = this.amorphic.config.nconf.get('insuredEsignKey');
    let data: ValidateDocumentAccess = {
      polno: '',
      isValid: false,
      signerFirstName: '',
      attemptsRemaining: 0,
      esignCompleted: false,
      urlExpired: false,
      recipient: '',
    };
    try {
      data = jwt.verify(token, secret) as ValidateDocumentAccess;
      data.isValid = true;
      LoggerSingleton.getInstance().info(
        {
          module: 'controller',
          function: 'validateInsuredEsignUrl',
          category: Category.request,
          context: {
            piiLevel: PiiLevelEnum.s1,
          },
          data: {
            token,
            data,
          },
        },
        'Insured token successfully validated'
      );
    } catch (err) {
      LoggerSingleton.getInstance().info(
        {
          module: 'controller',
          function: 'validateInsuredEsignUrl',
          category: Category.request,
          context: {
            piiLevel: PiiLevelEnum.s1,
          },
          data: {
            token,
          },
        },
        'Invalid token'
      );
      data.isValid = false;
    }

    if (data.isValid) {
      const policies = await this.getPolicy(data.polno);
      const policy = policies[0];
      data.esignCompleted = CoverpathUtils.isIssuingPolicy(policy) || CoverpathUtils.isPolicyInForce(policy);
      data.urlExpired = CoverpathUtils.isPolicyCanceled(policy);
      const signer = policy.insured.getInsuredSigner();
      data.signerFirstName = signer.firstName;
      data.attemptsRemaining = MAX_ATTEMPTS - signer.esignMetadata.validationAttempts;
    }

    LoggerSingleton.getInstance().info(
      {
        module: 'controller',
        function: 'validateInsuredEsignUrl',
        category: Category.request,
        context: {
          piiLevel: PiiLevelEnum.s1,
        },
        data: {
          token,
          ValidateDocumentAccess: data,
        },
      },
      'Insured ValidateDocumentAccess data'
    );
    return data;
  }

  @remote({ on: 'server' })
  async validateInsuredSSNAndGetDocsToSign(policyNumber: string, ssnLastFour: string) {
    const MAX_ATTEMPTS = 3;
    const data = {
      documents: [],
      attemptsRemaining: 0,
    };
    const policies = await this.getPolicy(policyNumber);
    const policy: Policy = policies[0];
    const signer = policy.insured.getInsuredSigner();
    let validationAttempts = signer.esignMetadata.validationAttempts || 0;
    data.attemptsRemaining = MAX_ATTEMPTS - validationAttempts;
    if (validationAttempts > 2) {
      LoggerSingleton.getInstance().info(
        {
          module: 'controller',
          function: 'validateInsuredSSNAndGetDocsToSign',
          category: Category.request,
          context: {
            piiLevel: PiiLevelEnum.nonPII,
            referenceType: 'Person',
            referenceId: signer._id,
          },
          data: {
            validationAttempts: data,
            policyNumber,
            partyId: signer._id,
          },
        },
        'Validation attempts exhausted'
      );

      return data;
    }

    const signerLast4 = await SecurityUtils.getPiiValueByKey(signer.SSN, 4);
    if (signerLast4 === ssnLastFour) {
      data.documents = await this.getInsuredDocNamesToBeSigned(policy);
      LoggerSingleton.getInstance().info(
        {
          module: 'controller',
          function: 'validateInsuredSSNAndGetDocsToSign',
          category: Category.request,
          context: {
            piiLevel: PiiLevelEnum.nonPII,
            referenceType: 'Person',
            referenceId: signer._id,
          },
          data: {
            validationAttempts: data,
            policyNumber,
            partyId: signer._id,
          },
        },
        'Successfully validated SSN'
      );

      return data;
    } else {
      validationAttempts++;
      await ESignSessionMetadata.getKnex().where('_id', '=', signer.esignMetadata._id).update({
        validationAttempts,
      });

      data.attemptsRemaining = MAX_ATTEMPTS - validationAttempts;
      LoggerSingleton.getInstance().info(
        {
          module: 'controller',
          function: 'validateInsuredSSNAndGetDocsToSign',
          category: Category.request,
          context: {
            piiLevel: PiiLevelEnum.nonPII,
            referenceType: 'Person',
            referenceId: signer._id,
          },
          data: {
            validationAttempts: data,
            policyNumber,
            partyId: signer._id,
          },
        },
        'Invalid SSN'
      );

      return data;
    }
  }

  private async getInsuredDocNamesToBeSigned(policy) {
    this.policyDocuments = await CoverpathUtilsBackEnd.getInsuredDocsToBeSigned(policy);
    return this.policyDocuments.map((doc) => {
      if (doc.name === CoverpathUtils.getAmendmentFormNameByState(policy.getApplicationState())) {
        return 'Amendment';
      } else if (doc.name === CoverpathUtils.getInsurabilityFormNameByState(policy.getApplicationState())) {
        return 'Insurability';
      } else if (doc.name === 'ConsentedApplication') {
        return 'Application';
      }
    });
  }

  private async getDocNamesToBeViewed(policy) {
    this.policyDocuments = await CoverpathUtilsBackEnd.getDocsToBeViewed(policy);
    return this.policyDocuments.map((doc) => {
      if (doc.name === 'ConsentedApplication') {
        return 'Application';
      }
    });
  }

  @remote({ on: 'server' })
  async insuredEsignComplete(token: string, policyNumber: string) {
    const secret: string = this.amorphic.config.nconf.get('insuredEsignKey');
    try {
      const data: jwt.JwtPayload = jwt.verify(token, secret) as jwt.JwtPayload;
      if (data.polno !== policyNumber) {
        LoggerSingleton.getInstance().info(
          {
            module: 'controller',
            function: 'insuredEsignComplete',
            category: Category.milestone,
            context: {
              piiLevel: PiiLevelEnum.s1,
            },
            data: {
              token,
              data,
              policyNumber,
            },
          },
          'Token and policyNumber do not match'
        );
        return false;
      }
    } catch (err) {
      LoggerSingleton.getInstance().info(
        {
          module: 'controller',
          function: 'insuredEsignComplete',
          category: Category.milestone,
          context: {
            piiLevel: PiiLevelEnum.s1,
          },
          data: {
            token,
          },
        },
        'Invalid token'
      );
      return false;
    }
    const policies = await this.getPolicy(policyNumber);
    const policy = policies[0];
    await Promise.all(
      this.policyDocuments.map(async (doc) => {
        let documentName = '';
        if (doc.name === CoverpathUtils.getAmendmentFormNameByState(policy.getApplicationState())) {
          documentName = 'Amendment';
        } else if (doc.name === CoverpathUtils.getInsurabilityFormNameByState(policy.getApplicationState())) {
          documentName = 'Insurability';
        }

        if (documentName) {
          await Policy.getKnex()
            .insert({
              _id: uuid.v4().toString(),
              _template: 'PolicyDocAction',
              __version__: 1,
              party_id: policy.insured.getInsuredSigner()._id,
              policy_doc_id: doc._id,
              policy_doc_name: doc.name,
              comment: `Insured/Signer Esigned`,
              action: POLICY_DOC_ACTION_TYPE.SIGNED,
              ipAddress: this.amorphic['incomingIP'],
            })
            .into('policy_doc_action');
        }

        return documentName;
      })
    );

    const signerPartyId = policy.insured.getInsuredSigner()._id;
    LoggerSingleton.getInstance().info(
      {
        module: 'controller',
        function: 'insuredEsignComplete',
        category: Category.milestone,
        context: {
          piiLevel: PiiLevelEnum.nonPII,
          referenceType: 'Person',
          referenceId: signerPartyId,
        },
        data: {
          signerPartyId,
        },
      },
      'Insured finished esign'
    );
    await policy.fulfillRequirements(['LI1R14 Insured finished Esign', 'MI1R13 Insured finished Esign']);

    return true;
  }

  @remote({ on: 'server' })
  async markEsignComplete(token: string, appType: string) {
    const { polno, recipient } = await this.parseAnnuitytoken(token);
    const contracts = await Policy.persistorFetchByQuery({ policyNumber: polno });
    const contract = contracts[0];
    const doc: PolicyDoc = await this.accountCenterController.getSinglePolicyDoc(contract, appType, appType);
    const signer = this.getAnnuityCosigner(recipient, contract);
    if (doc) {
      await this.accountCenterController.signAnnuityForm(contract, appType, signer.getFullName(), recipient);
      LoggerSingleton.getInstance().info(
        {
          module: 'controller',
          function: 'markEsignComplete',
          category: Category.milestone,
          context: {
            piiLevel: PiiLevelEnum.nonPII,
            referenceType: 'Person',
            referenceId: signer._id,
          },
          data: {
            signerPartyId: signer._id,
            polno,
            recipient,
            appType,
          },
        },
        'esign complete'
      );

      return true;
    } else {
      LoggerSingleton.getInstance().warn(
        {
          module: 'controller',
          function: 'markEsignComplete',
          category: Category.milestone,
          context: {
            piiLevel: PiiLevelEnum.nonPII,
            referenceType: 'Person',
            referenceId: signer._id,
          },
          data: {
            signerPartyId: signer._id,
            polno,
            recipient,
            appType,
          },
        },
        'Doc not found'
      );
      return false;
    }
  }

  @remote({ on: 'server' })
  async insuredEsignCompleteForAutomation() {
    if (Utils.isProdEnv()) {
      return;
    }
    const policies = await this.getPolicy(this.customer.application.primaryPolicy.policyNumber);
    const policy = policies[0];
    await policy.fulfillRequirements(['LI1R14 Insured finished Esign', 'MI1R13 Insured finished Esign']);
    LoggerSingleton.getInstance().warn(
      {
        module: 'controller',
        function: 'insuredEsignCompleteForAutomation',
        category: Category.request,
        data: {
          policyNumber: policy.policyNumber,
        },
      },
      'Insured finished esign'
    );

    return true;
  }

  /**
   * This function is to expose basic illustration html for qa automation in dev and uat env
   * selenium test can now access the html by using "this.controller.btHtmlStr"
   */
  @remote({ on: 'server' })
  async saveBasicIllustrationHtml(email: string) {
    if (this.nonProdMode) {
      const query = {
        email: email,
      };

      const fetch = {
        insuranceQuotes: true,
        applications: true,
      };
      const results = await CoverpathCustomer.persistorFetchByQuery(query, {
        fetch,
        session: this.amorphic,
      });
      const customer: CoverpathCustomer = results[0];

      const request: IllustrationRequest = this.htmlPdfController.createMostRecentAppIllustrationRequest(
        customer,
        customer.application.primaryPolicy.primaryPolicyAgent.agent
      );
      const response = await this.htmlPdfController.generateBasicIllustrationHTML(request, false);
      this.biHtmlStr = response.map((body) => body.html).join();
    }
  }

  @remote({ on: 'server' })
  public async getPolicyDetails(policy: CoverpathBasePolicy) {
    return PolicyAdminClient.getPolicyAdminPolicy(policy);
  }

  @remote({ on: 'server' })
  public async setPiiByProperty(prop: string, attrValue: string): Promise<string> {
    //A customer can always set PII data on their own policies as long as they are logged in.
    if (this.loggedIn) {
      const userInfo: LoggedInUserInfo = this.getLoggedInUserInfo();
      return SecurityUtils.setPiiByProperty(prop, attrValue, userInfo, this.customer.application.primaryPolicy);
    }
  }

  @remote({ on: 'server' })
  public async getPiiValueByKey(
    attrKey: string,
    lastDigits: number = 0,
    includeHyphen: boolean = true,
    attrName: string = 'SSN'
  ): Promise<string> {
    if (
      this.loggedIn &&
      this.customer &&
      this.customer.application &&
      this.customer.application.primaryPolicy &&
      SecurityUtils.isPiiKeyOnApplication(attrKey, this.customer.application)
    ) {
      return SecurityUtils.getPiiValueByKey(attrKey, lastDigits, includeHyphen, attrName);
    } else {
      LoggerSingleton.getInstance().error(
        {
          module: 'CoverpathClientController',
          function: 'getPiiValueByKey',
          category: Category.security,
          context: {
            piiLevel: PiiLevelEnum.nonPII,
            referenceType: 'Customer',
            referenceId: this.customer._id,
          },
          error: {
            isHumanRelated: false,
          },
        },
        `${
          this.customer._id ? 'Customer ' + this.customer._id : 'Unauthenticated user'
        } attempted to get pii data for key ${attrKey}`
      );
    }
  }

  @remote({ on: 'server' })
  public async handlePiiAfterAutoFill(): Promise<void> {
    if (this.loggedIn) {
      const loggedInUserInfo: LoggedInUserInfo = this.getLoggedInUserInfo();
      await SecurityUtils.clonePiiByApplication(this.customer.application, loggedInUserInfo);
    }
  }

  @serverOnly()
  public async handlePiiAfterApplicationClone(): Promise<void> {
    if (this.loggedIn) {
      const loggedInUserInfo: LoggedInUserInfo = this.getLoggedInUserInfo();
      await SecurityUtils.clonePiiByApplication(this.customer.getLatestApplication(), loggedInUserInfo);
    }
  }

  @remote({ on: 'server' })
  async setPiiByPersonId(personId: string, attrName: string, attrValue: string): Promise<string> {
    if (this.loggedIn) {
      const userInfo: LoggedInUserInfo = this.getLoggedInUserInfo();
      return SecurityUtils.setPiiByPersonId(personId, attrName, attrValue, userInfo);
    }
  }

  @remote({ on: 'server' })
  public async getPiiByPersonId(personId: string, attrName: string, lastDigits: number = 0): Promise<string> {
    if (this.loggedIn) {
      return SecurityUtils.getPiiByPersonId(personId, attrName, lastDigits);
    }
  }

  @serverOnly()
  private getLoggedInUserInfo(): LoggedInUserInfo {
    const loggedInUserInfo: LoggedInUserInfo = {
      userId: this.customer._id,
      entitlement: UserEntitlement.CUSTOMER,
    };
    return loggedInUserInfo;
  }
}
